import {loadMedicineCatalogue} from '@/services/medicineCatalogue.mjs';
import ClinicalActionPanel from "@/features/clinical-actions/ClinicalActionPanel.jsx";
import { loadClinicalAction } from "@/services/clinicalActionService.js";
// src/pages/appointments/prescriptions/DoctorPrescriptionWizard.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./DoctorPrescriptionWizard.css";
import { selectRxFrequencyLabel } from "@/utils/rxLabelUtils";
import DatePicker from "@/sot/primitives/datePicker.jsx";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import TopBarRx from "@/sot/primitives/TopBarRx.jsx";
import Sheet from "@/sot/primitives/Sheet.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";

import Divider from "@/components/ui/Divider.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import DoctorSignatureCaptureCard from "@/features/doctor/DoctorSignatureCaptureCard.jsx";
import QrOverlay, { DoctorStamp } from "@/components/prescriptions/QrOverlay.jsx";
import SignatureStampLine from "@/components/prescriptions/SignatureStampLine.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import {
  selectDoctorDisplayName,
  selectDoctorLicenseNumber,
  selectDoctorPrescriptionName,
} from "@/utils/prescriptionDoctorIdentity.js";
import {
  buildPrescriptionVerifySnapshot,
  buildPrescriptionVerifyPath,
  buildPrescriptionVerifyState,
  buildPrescriptionVerifyUrl,
} from "@/utils/prescriptionVerification.js";
import {
  buildPrescriptionDosageLabel,
} from "@/utils/prescriptionSchedule.js";
import { resolveFacilityAddress } from "@/utils/prescriptionFacility.js";
import { makeBrandedQrDataUrl } from "@/utils/brandedQr.js";

const ICONS = {
  SEARCH: "/icons/appointments/dr-e-prescription/search.svg",
  CANCEL: "/icons/appointments/dr-e-prescription/cancel.svg",
  ADD_TWO: "/icons/appointments/dr-e-prescription/add_2.svg",

  // Sheet icons
  IMAGESMODE: "/icons/appointments/dr-e-prescription/imagesmode.svg",
  PHOTO_CAMERA: "/icons/appointments/dr-e-prescription/photo_camera.svg",
  NAVIGATE_NEXT: "/icons/appointments/dr-e-prescription/navigate_next.svg",
  ELLIPSIS_VERTICAL: "/icons/appointments/dr-e-prescription/ellipsis-vertical.svg",

  // Medication type icons
  CAPSULE: "/icons/appointments/dr-e-prescription/capsule.svg",
  INJECTION_SOLUTION: "/icons/appointments/dr-e-prescription/injection_solution.svg",
  ORAL_SOLUTION: "/icons/appointments/dr-e-prescription/oral_solution.svg",
  TOPICAL: "/icons/appointments/dr-e-prescription/topical.svg",
  DEVICE: "/icons/appointments/dr-e-prescription/device.svg",
  DROPS: "/icons/appointments/dr-e-prescription/drops.svg",
  INHALER: "/icons/appointments/dr-e-prescription/Inhaler.svg",
  TABLET: "/icons/appointments/dr-e-prescription/tablet.svg",

  // Frequency / Time section icons
  FREQUENCY: "/icons/appointments/dr-e-prescription/frequency.svg",
  TIME: "/icons/appointments/dr-e-prescription/time.svg",

  // Duration / cycle icons
  DATE_RANGE_FILL: "/icons/appointments/dr-e-prescription/date_range_Fill.svg",
  ICON_QR: "/icons/appointments/e-prescription/qr-code-filled.svg",

};

const MED_DETAILS_CSV_URL = "/data/Medicine_Details.csv";
const PRESCRIPTION_BG_LIGHT = "/assets/images/rx-wizard-cover.png";
const PRESCRIPTION_BG_DARK = "/assets/images/rx-wizard-cover.png";

const SIG_PLACEHOLDER = "/icons/appointments/e-prescription/Signature.svg";
const ICON_SIGN = "/icons/appointments/dr-e-prescription/signature.svg";
const ICON_QR = "/icons/appointments/e-prescription/qr-code-filled.svg";
const QR_ELEPHANT = "/afrimed_elephant_icon.svg";


// ✅ We only use the BASE elephant PNG + vector rings/text overlay (DoctorStamp)
// (The fully baked SVG/PNG stamp assets are intentionally not used to avoid double layers)
const DR_STAMP_BASE = "/icons/appointments/e-prescription/afrimed-dr-stamp-base-flat.png";

const abs = (p) => (p?.startsWith("http") ? p : `${window.location.origin}${p}`);

const DEV_SIG_KEY = "afrimed:dev:doctorSignatureDataUrl";
const DEV_ISSUED_KEY_PREFIX = "afrimed:dev:rx:issued:";

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "")
  );
}

// -------------------------
// Draft shape (SSOT)
// -------------------------
function makeEmptyDraft(appointmentId) {
  const nowIso = new Date().toISOString();
  return {
    version: 1,
    appointmentId: String(appointmentId),

    doctor: { id: "", name: "", regNo: "", signatureSrc: "" },
    patient: { id: "", name: "", age: null, sex: "" },

    images: [],

    medicationQuery: "",
    medication: {
      id: "",
      name: "",
      composition: "",
      type: "",
      strengthValue: "",
      strengthUnit: "mg",
      uses: "",
      sideEffects: "",
      imageUrl: "",
      manufacturer: "",
      note: "",
    },
    medications: [],

    schedule: {
      frequencyType: "",

      everyHours: 8,
      dosesPerTime: "1",
      doseUnitLabel: "Capsule",

      specificDays: [],

      cycleUseDays: 15,
      cyclePauseDays: 7,

      startDateIso: "",
      endDateIso: "",
    },

    diagnosisTitle: "",
    diagnosisNotes: "",
    notes: "",
    status: "draft",
    createdAtIso: nowIso,
    updatedAtIso: nowIso,
  };
}

function draftKey(appointmentId) {
  return `afrimed:rx:draft:${String(appointmentId)}`;
}

function loadDraft(appointmentId) {
  const key = draftKey(appointmentId);
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.appointmentId === String(appointmentId)) {
        return { ...makeEmptyDraft(appointmentId), ...parsed };
      }
    }
  } catch {
    // ignore
  }

  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.appointmentId === String(appointmentId)) {
        return { ...makeEmptyDraft(appointmentId), ...parsed };
      }
    }
  } catch {
    // ignore
  }

  return makeEmptyDraft(appointmentId);
}

function saveDraft(d) {
  const key = draftKey(d.appointmentId);
  try {
    const raw = JSON.stringify(d);
    sessionStorage.setItem(key, raw);
    localStorage.setItem(key, raw);
  } catch {
    // ignore
  }
}

// -------------------------
// Text helpers
// -------------------------
function truncateOneLineStyle() {
  return { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
}

function ActionIconButton({ icon, label, onClick, className = "" }) {
  const { isDark } = useTheme();
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={[
        "inline-flex items-center justify-center",
        "h-10 w-10 rounded-[12px]",
        "hover:opacity-100 opacity-90",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(2,6,23,0.18)]",
        "transition-opacity",
        className,
      ].join(" ")}
    >
      <img
        src={icon}
        alt=""
        className="h-5 w-5"
        draggable="false"
        style={
          isDark
            ? { filter: "brightness(0) saturate(100%) invert(80%) sepia(8%) saturate(348%) hue-rotate(182deg) brightness(89%) contrast(87%)" }
            : undefined
        }
      />
    </button>
  );
}

function normName(s) {
  return String(s || "").trim().toLowerCase();
}

function decodeHtmlEntities(s) {
  return String(s || "")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

function formatDateDisplay(isoOrYmd) {
  const v = String(isoOrYmd || "").trim();
  if (!v) return "";
  // Accept YYYY-MM-DD or ISO
  const ymd = v.length >= 10 ? v.slice(0, 10) : v;
  const [y, m, d] = ymd.split("-").map((x) => Number(x));
  if (!y || !m || !d) return ymd;

  const languageCode =
    typeof document !== "undefined"
      ? String(document.documentElement.getAttribute("lang") || "").slice(0, 2).toLowerCase()
      : "";
  const months =
    languageCode === "sn"
      ? ["Ndira", "Kukadzi", "Kurume", "Kubvumbi", "Chivabvu", "Chikumi", "Chikunguru", "Nyamavhuvhu", "Gunyana", "Gumiguru", "Mbudzi", "Zvita"]
      : languageCode === "zu"
        ? ["Januwari", "Febhuwari", "Mashi", "Ephreli", "Meyi", "Juni", "Julayi", "Agasti", "Septhemba", "Okthoba", "Novemba", "Disemba"]
        : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d} ${months[m - 1]} ${y}`;
}

function uniqueIds(...values) {
  return Array.from(
    new Set(
      values
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
}

function firstText(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (text) return text;
  }
  return "";
}

async function snapshotSignatureImage(src) {
  const value = String(src || "").trim();
  if (!value || value === SIG_PLACEHOLDER || value.startsWith("data:")) {
    return value;
  }

  try {
    const response = await fetch(value, { cache: "no-store" });
    if (!response.ok) throw new Error(`signature_fetch_${response.status}`);
    const blob = await response.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("signature_read_failed"));
      reader.readAsDataURL(blob);
    });
    return String(dataUrl || "").trim() || value;
  } catch {
    return value;
  }
}


// -------------------------
// CSV loader (minimal + safe)
// -------------------------
function parseCSV(text) {
  const rows = [];
  const lines = (text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  if (!lines.length) return rows;

  function splitLine(line) {
    const out = [];
    let cur = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];

      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
        continue;
      }
      if (ch === '"') {
        inQuotes = !inQuotes;
        continue;
      }
      if (ch === "," && !inQuotes) {
        out.push(cur.trim());
        cur = "";
        continue;
      }
      cur += ch;
    }
    out.push(cur.trim());
    return out.map((v) => v.replace(/^"|"$/g, "").trim());
  }

  const header = splitLine(lines[0] || "");
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line || !line.trim()) continue;
    const cols = splitLine(line);
    const obj = {};
    header.forEach((h, idx) => {
      obj[h] = cols[idx] ?? "";
    });
    rows.push(obj);
  }
  return rows;
}

function pick(obj, keys) {
  for (const k of keys) {
    if (obj && typeof obj[k] !== "undefined" && obj[k] !== null && String(obj[k]).trim() !== "") {
      return String(obj[k]).trim();
    }
  }
  return "";
}

function extractMedicationStrength(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (!text) continue;

    const match = text.match(/(\d+(?:\.\d+)?)\s*(mg|mcg|g|ml|iu|%)\b/i);
    if (!match) continue;

    return {
      value: match[1],
      unit: match[2].toLowerCase(),
    };
  }

  return null;
}

// -------------------------
// UI helpers
// -------------------------
function Radio({ active }) {
  // ✅ shrink-proof (fixes squashed radios in tight rows)
  return (
    <div className="shrink-0 flex items-center justify-center" style={{ width: 24, height: 24 }}>
      <div
        className="flex items-center justify-center rounded-full"
        style={{
          width: 20,
          height: 20,
          boxShadow: active
            ? "0 0 0 2px rgba(29,79,215,0.85) inset"
            : "0 0 0 2px rgba(91,107,134,0.35) inset",
          background: "transparent",
        }}
      >
        {active ? (
          <div className="rounded-full" style={{ width: 10, height: 10, background: "rgb(var(--afrimed-primary))" }} />
        ) : null}
      </div>
    </div>
  );
}

// -------------------------
// Types + Units
// -------------------------
function getMedicationTypeOptions(t) {
  return [
    { key: "capsule", label: t("prescriptions.wizard.medType.capsule", "Capsule"), icon: ICONS.CAPSULE },
    { key: "tablet", label: t("prescriptions.wizard.medType.tablet", "Tablet"), icon: ICONS.TABLET },
    {
      key: "injection_solution",
      label: t("prescriptions.wizard.medType.injectionSolution", "Injection solution"),
      icon: ICONS.INJECTION_SOLUTION,
    },
    { key: "oral_solution", label: t("prescriptions.wizard.medType.oralSolution", "Oral solution"), icon: ICONS.ORAL_SOLUTION },
    { key: "topical", label: t("prescriptions.wizard.medType.topical", "Topical"), icon: ICONS.TOPICAL },
    { key: "device", label: t("prescriptions.wizard.medType.device", "Device"), icon: ICONS.DEVICE },
    { key: "drops", label: t("prescriptions.wizard.medType.drops", "Drops"), icon: ICONS.DROPS },
    { key: "inhaler", label: t("prescriptions.wizard.medType.inhaler", "Inhaler"), icon: ICONS.INHALER },
  ];
}

const UNITS = ["mg", "mcg", "g", "mL", "%"];

function getFrequencyOptions(t) {
  return [
    { key: "daily", label: t("prescriptions.wizard.frequency.daily", "Daily") },
    { key: "cyclical", label: t("prescriptions.wizard.frequency.cyclical", "Cyclical schedule") },
    { key: "specific_days", label: t("prescriptions.wizard.frequency.specificDays", "On a specific day of the week") },
    { key: "as_needed", label: t("prescriptions.wizard.frequency.asNeeded", "As needed") },
  ];
}

function getDayOptions(t) {
  return [
    { key: "mon", label: t("prescriptions.wizard.days.mon", "Mon") },
    { key: "tue", label: t("prescriptions.wizard.days.tue", "Tue") },
    { key: "wed", label: t("prescriptions.wizard.days.wed", "Wed") },
    { key: "thu", label: t("prescriptions.wizard.days.thu", "Thu") },
    { key: "fri", label: t("prescriptions.wizard.days.fri", "Fri") },
    { key: "sat", label: t("prescriptions.wizard.days.sat", "Sat") },
    { key: "sun", label: t("prescriptions.wizard.days.sun", "Sun") },
  ];
}

// Step7 & 8 Functions //

function formatSpecificDays(days, dayShortMap) {
  const list = (days || [])
    .map((d) => String(d || "").toLowerCase().slice(0, 3))
    .map((k) => dayShortMap[k])
    .filter(Boolean);

  // de-dupe but preserve order
  return [...new Set(list)].join(", ");
}

// -------------------------
// Wizard
// -------------------------
export default function DoctorPrescriptionWizard() {
const { isDark } = useTheme();
const { t, languageCode } = useLanguage();
const [dateSheet, setDateSheet] = React.useState(null); // "start" | "end" | null
const [dateDraft, setDateDraft] = React.useState(""); // YYYY-MM-DD

function openStartDatePicker() {
  const v = String(draft?.schedule?.startDateIso || "").slice(0, 10);
  setDateDraft(v);
  setDateSheet("start");
}

function openEndDatePicker() {
  const v = String(draft?.schedule?.endDateIso || "").slice(0, 10);
  setDateDraft(v);
  setDateSheet("end");
}

function closeDatePicker() {
  setDateSheet(null);
}

  const nav = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams();
  const clinicalActionId = new URLSearchParams(location.search).get("clinicalActionId");

  const [step, setStep] = React.useState(() => {
  try {
    const raw = sessionStorage.getItem(stepKey(appointmentId));
    const n = Number(raw);
    return [0, 1, 2, 3, 41].includes(n) ? n : 0;
  } catch {
    return 0;
  }
});

React.useEffect(() => {
  try {
    sessionStorage.setItem(stepKey(appointmentId), String(step));
  } catch {
    // ignore
  }
}, [appointmentId, step]);

const STEP_BUILDER = 1;
const STEP_REVIEW = 2;
const STEP_SIGN = 3;
const STEP_UNIT = 41; // keep if you still use it

  // ✅ Draft MUST exist before anything tries to read it
  const [draft, setDraft] = React.useState(() => loadDraft(appointmentId));

  const [imagesSheetOpen, setImagesSheetOpen] = React.useState(false);

  const startYmd = draft.schedule?.startDateIso ? String(draft.schedule.startDateIso).slice(0, 10) : "";
  const endYmd = draft.schedule?.endDateIso ? String(draft.schedule.endDateIso).slice(0, 10) : "";

  const startDisplay = startYmd ? formatDateDisplay(startYmd) : t("prescriptions.wizard.select", "Select");
  const endDisplay = endYmd ? formatDateDisplay(endYmd) : t("prescriptions.wizard.select", "Select");

  // Choose Unit sheet
  const [unitSheetOpen, setUnitSheetOpen] = React.useState(false);
  const [unitSearch, setUnitSearch] = React.useState("");
  const [unitDraft, setUnitDraft] = React.useState("mg");
  
  const [medRows, setMedRows] = React.useState([]);
  const [medLoading, setMedLoading] = React.useState(false);
  const medTypeOptions = React.useMemo(() => getMedicationTypeOptions(t), [t]);
  const frequencyOptions = React.useMemo(() => getFrequencyOptions(t), [t]);
  const dayOptions = React.useMemo(() => getDayOptions(t), [t]);
  const dayShortMap = React.useMemo(
    () => Object.fromEntries(dayOptions.map((day) => [day.key, day.label])),
    [dayOptions],
  );

  const galleryInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);

  const [notes, setNotes] = React.useState(() => loadDraft(appointmentId)?.notes || "");
  React.useEffect(() => {
    if (!clinicalActionId) return;
    let alive = true;
    loadClinicalAction(clinicalActionId).then(action => {
      if (alive && action.appointment_id === appointmentId && action.kind === 'prescription' && action.state === 'reviewed') setNotes(current => current || action.content.text || '');
    }).catch(() => {});
    return () => { alive = false; };
  }, [clinicalActionId, appointmentId]);
  const [issueBusy, setIssueBusy] = React.useState(false);
  const [issueErr, setIssueErr] = React.useState("");
  const [signaturePersisted, setSignaturePersisted] = React.useState(false);
  const [issuedPrescriptionId, setIssuedPrescriptionId] = React.useState(() => {
    const maybeId = loadDraft(appointmentId)?.id || loadDraft(appointmentId)?.prescriptionId || "";
    return isUuid(maybeId) ? String(maybeId) : "";
  });

  React.useEffect(() => {
    const routeState = location.state || {};
    const restored = routeState?.restored || routeState;
    if (!restored || typeof restored !== "object") return;

    // Appointment scheduling/diagnosis snapshots are not approved clinical notes.
    // Reviewed context is applied through the governed assistance panel.
    const nextDiagnosisTitle = '';
    const nextDiagnosisNotes = '';
    const nextPatientName = String(
      routeState?.patient?.full_name ||
        routeState?.patient?.name ||
        restored?.patient?.full_name ||
        restored?.patient?.name ||
        ""
    ).trim();
    const nextPatientAge =
      routeState?.patient?.age ??
      restored?.patient?.age ??
      restored?.age ??
      null;
    const nextPatientSex = String(
      routeState?.patient?.gender ||
        routeState?.patient?.sex ||
        restored?.patient?.gender ||
        restored?.patient?.sex ||
        restored?.sex ||
        ""
    ).trim();

    setDraft((current) => {
      const merged = {
        ...current,
        diagnosisTitle: current?.diagnosisTitle || nextDiagnosisTitle,
        diagnosisNotes: current?.diagnosisNotes || nextDiagnosisNotes,
        notes: current?.notes || nextDiagnosisNotes,
        patient: {
          ...(current?.patient || {}),
          id:
            current?.patient?.id ||
            routeState?.patientId ||
            routeState?.patient?.id ||
            routeState?.patient?.user_id ||
            restored?.patient?.id ||
            restored?.patient?.user_id ||
            null,
          name: current?.patient?.name || nextPatientName,
          age: current?.patient?.age ?? nextPatientAge,
          sex: current?.patient?.sex || nextPatientSex,
        },
      };

      return JSON.stringify(merged) === JSON.stringify(current) ? current : merged;
    });

    setNotes((current) => current || nextDiagnosisNotes);
  }, [location.state]);
  const [issueDone, setIssueDone] = React.useState(() => {
    const maybeId = loadDraft(appointmentId)?.id || loadDraft(appointmentId)?.prescriptionId || "";
    return isUuid(maybeId);
  });

  const buildIssuedPrescriptionState = React.useCallback(
    (prescriptionId) => {
      const nextPrescriptionId = String(prescriptionId || "").trim();
      const patientIdFromState =
        draft?.patient?.id ||
        location.state?.patientId ||
        location.state?.patient?.id ||
        location.state?.patient?.user_id ||
        null;
      const restoredState = {
        ...(location.state?.restored || {}),
        prescriptionId: nextPrescriptionId || location.state?.restored?.prescriptionId || null,
        supportConversationId: null,
      };

      return {
        from: "doctor-rx-wizard",
        actorRole: "doctor",
        appointmentId: String(appointmentId),
        prescriptionId: nextPrescriptionId || null,
        supportConversationId: null,
        patientId: patientIdFromState,
        patient: location.state?.patient || null,
        restored: restoredState,
        returnTo:
          location.state?.returnTo ||
          `/doctors/appointments/${String(appointmentId)}`,
        returnState:
          location.state?.returnState || {
            actorRole: "doctor",
            appointmentId: String(appointmentId),
            patientId: patientIdFromState,
            patient: location.state?.patient || null,
            restored: restoredState,
            prescriptionId: nextPrescriptionId || null,
          },
      };
    },
    [appointmentId, draft?.patient?.id, location.state]
  );

  // -------------------------
  // DEV MODE (no auth) helpers
  // -------------------------
  const [devSigSrc, setDevSigSrc] = React.useState(() => {
    try {
      return sessionStorage.getItem(DEV_SIG_KEY) || "";
    } catch {
      return "";
    }
  });

  const doctor = React.useMemo(() => {
    const sig =
      draft?.doctor?.signatureSrc ||
      devSigSrc || // ✅ DEV override
      null;

    return {
      name: draft?.doctor?.name || "Dr. Thato M. Dube",
      regNo: draft?.doctor?.regNo || "1234567890",
      signatureSrc: sig,
    };
  }, [draft?.doctor?.name, draft?.doctor?.regNo, draft?.doctor?.signatureSrc, devSigSrc]);
  const doctorPrescriptionName = React.useMemo(
    () => selectDoctorPrescriptionName(doctor) || doctor.name,
    [doctor]
  );
  const verifyFacility = React.useMemo(() => {
    const restored = location.state?.restored || location.state || {};
    const facility = restored?.facility || location.state?.facility || {};

    return {
      name: firstText(
        restored?.facilityName,
        facility?.name,
        restored?.hospital,
        restored?.clinicName,
        restored?.practiceName,
      ),
      address: resolveFacilityAddress({
        addressCandidates: [
          restored?.facilityAddress,
          facility?.address,
          restored?.address,
        ],
        postalCodeCandidates: [
          restored?.facilityPostalCode,
          restored?.facilityPostCode,
          restored?.postal_code,
          restored?.postalCode,
          restored?.zip_code,
          restored?.zipCode,
          facility?.postal_code,
          facility?.postalCode,
          facility?.zip_code,
          facility?.zipCode,
        ],
        cityCandidates: [
          restored?.facilityCity,
          facility?.city,
        ],
        countryCandidates: [
          restored?.facilityCountry,
          facility?.country,
        ],
      }),
      postalCode: firstText(
        restored?.facilityPostalCode,
        restored?.facilityPostCode,
        restored?.postal_code,
        restored?.postalCode,
        restored?.zip_code,
        restored?.zipCode,
        facility?.postal_code,
        facility?.postalCode,
        facility?.zip_code,
        facility?.zipCode,
      ),
      city: firstText(
        restored?.facilityCity,
        facility?.city,
      ),
      country: firstText(
        restored?.facilityCountry,
        facility?.country,
      ),
      phone: firstText(
        restored?.facilityPhone,
        facility?.phone,
        restored?.phone,
      ),
      email: firstText(
        restored?.facilityEmail,
        facility?.email,
        restored?.email,
      ),
    };
  }, [location.state]);
  const verifyPatient = React.useMemo(() => {
    const restored = location.state?.restored || location.state || {};
    const patient = location.state?.patient || restored?.patient || {};

    return {
      name: firstText(
        draft?.patient?.name,
        patient?.full_name,
        patient?.name,
      ),
      phone: firstText(
        patient?.phone,
        restored?.patientPhone,
        restored?.phone,
      ),
      email: firstText(
        patient?.email,
        restored?.patientEmail,
      ),
      address: firstText(
        patient?.address,
        restored?.patientAddress,
        restored?.address,
      ),
      dob: firstText(
        patient?.date_of_birth,
        patient?.dob,
        restored?.patientDob,
        restored?.date_of_birth,
      ),
      nationalId: firstText(
        patient?.national_id_number,
        patient?.national_id,
        restored?.patientNationalId,
        restored?.national_id_number,
        restored?.national_id,
      ),
    };
  }, [draft?.patient?.name, location.state]);

  const previewMedication = Array.isArray(draft?.medications) && draft.medications.length
    ? draft.medications[0]
    : null;
  const rxName = previewMedication?.name || draft.medication?.name || "";
  const rxTypeLabel =
    previewMedication?.typeLabel ||
    medTypeOptions.find((option) => option.key === draft.medication?.type)?.label ||
    "";

  // Keep dose unit label aligned with medication type
  React.useEffect(() => {
    const t = (draft.medication?.type || "").toLowerCase();
    const label = medTypeOptions.find((x) => x.key === t)?.label;
    if (!label) return;
    setDraft((d) => ({
      ...d,
      schedule: { ...d.schedule, doseUnitLabel: label },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.medication?.type]);

  const [qrOpen, setQrOpen] = React.useState(false);
  const [qrSrc, setQrSrc] = React.useState(null);

const rx = React.useMemo(() => {
  const medName = previewMedication?.name || draft?.medication?.name || rxName || t("prescriptions.wizard.medication", "Medication");
  const form =
    previewMedication?.typeLabel ||
    draft?.medication?.strengthLabel ||
    draft?.medication?.form ||
    "";
  const ingredients = previewMedication?.composition || draft?.medication?.composition || draft?.medication?.ingredients || "";

  return {
    medicineName: medName,
    form,
    ingredients,
    doctor, // uses your existing `doctor` memo from Step8
  };
}, [
  draft?.medication?.name,
  draft?.medication?.strengthLabel,
  draft?.medication?.form,
  draft?.medication?.composition,
  draft?.medication?.ingredients,
  previewMedication,
  rxName,
  doctor,
]);

  React.useEffect(() => {
    // keep in sync if another screen sets it
    const t = setInterval(() => {
      try {
        const v = sessionStorage.getItem(DEV_SIG_KEY) || "";
        setDevSigSrc((prev) => (prev === v ? prev : v));
      } catch {
        // ignore
      }
    }, 600);
    return () => clearInterval(t);
  }, []);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const { data: authRes } = await supabase.auth.getUser();
        const user = authRes?.user;
        if (!user || !alive) return;

        const [{ data: profileRow }, { data: doctorRow }] = await Promise.all([
          supabase
            .from("profiles")
            .select("display_name, full_name, first_name, last_name")
            .eq("id", user.id)
            .maybeSingle(),
          supabase
            .from("doctor_profiles")
            .select("full_name, register_number, medical_license_number, signature_path")
            .eq("user_id", user.id)
            .maybeSingle(),
        ]);

        let signatureUrl = "";
        const signaturePath = doctorRow?.signature_path || "";
        if (signaturePath) {
          const { data: signed } = await supabase.storage
            .from("doctor-assets")
            .createSignedUrl(signaturePath, 60 * 10);
          signatureUrl = signed?.signedUrl || "";
        }

        if (!alive) return;

        if (signatureUrl) setSignaturePersisted(true);

        setDraft((d) => {
          const currentName = String(d?.doctor?.name || "").trim();
          const currentReg = String(d?.doctor?.regNo || "").trim();
          const currentSig = String(d?.doctor?.signatureSrc || "").trim();

          const hydratedName = selectDoctorDisplayName(doctorRow, profileRow);
          const hydratedRegNo = selectDoctorLicenseNumber(doctorRow, profileRow);
          const nextName =
            currentName && currentName !== "Dr. Thato M. Dube"
              ? currentName
              : hydratedName || currentName || "Dr. Thato M. Dube";
          const nextReg =
            currentReg && currentReg !== "1234567890"
              ? currentReg
              : hydratedRegNo || currentReg || "1234567890";
          const nextSig = currentSig || signatureUrl || "";

          if (
            nextName === currentName &&
            nextReg === currentReg &&
            nextSig === currentSig &&
            d?.doctor?.id === user.id
          ) {
            return d;
          }

          return {
            ...d,
            doctor: {
              ...(d.doctor || {}),
              id: user.id,
              name: nextName,
              regNo: nextReg,
              signatureSrc: nextSig,
            },
          };
        });
      } catch {
        // best-effort hydration only
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  function devIssueSnapshot(payload) {
    try {
      sessionStorage.setItem(`${DEV_ISSUED_KEY_PREFIX}${payload.rxId}`, JSON.stringify(payload));
    } catch {
      // ignore
    }
  }

  function nowIso() {
    return new Date().toISOString();
  }

function readFileAsDataUrl(file) {
  return new Promise((resolve) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => resolve("");
    r.readAsDataURL(file);
  });
}

const openGalleryPicker = () => galleryInputRef.current?.click();
const openCameraPicker = () => cameraInputRef.current?.click();

const [filePickTarget, setFilePickTarget] = React.useState(null); // "rx_images" | "signature" | null

// Use real id if you have it; fallback keeps prototype stable
const rxId = React.useMemo(() => {
  return issuedPrescriptionId || draft?.id || draft?.rxNo || draft?.prescriptionId || `rx-${Date.now()}`;
}, [issuedPrescriptionId, draft?.id, draft?.rxNo, draft?.prescriptionId]);
const verifyPrescriptionId = React.useMemo(
  () => issuedPrescriptionId || "",
  [issuedPrescriptionId]
);

const dosageLabel = React.useMemo(
  () => buildPrescriptionDosageLabel(draft, { t, languageCode }),
  [draft, languageCode, t]
);

const savedMedications = Array.isArray(draft.medications) ? draft.medications : [];
const uploadedImages = Array.isArray(draft.images)
  ? draft.images.filter((item) => typeof item?.src === "string" && item.src)
  : [];
const typeLabelMap = React.useMemo(
  () => Object.fromEntries(medTypeOptions.map((option) => [option.key, option.label])),
  [medTypeOptions],
);
const currentMedicationTypeLabel = typeLabelMap[draft?.medication?.type] || "";

function resetMedicationEditor(options = {}) {
  const keepQuery = options.keepQuery ? String(draft.medicationQuery || "") : "";
  setDraft((d) => ({
    ...d,
    medicationQuery: keepQuery,
    medication: {
      id: "",
      name: "",
      composition: "",
      type: "",
      strengthValue: "",
      strengthUnit: "mg",
      uses: "",
      sideEffects: "",
      imageUrl: "",
      manufacturer: "",
      note: "",
    },
    schedule: {
      frequencyType: "",
      everyHours: 8,
      dosesPerTime: "1",
      doseUnitLabel: "Capsule",
      specificDays: [],
      cycleUseDays: 15,
      cyclePauseDays: 7,
      startDateIso: "",
      endDateIso: "",
    },
    updatedAtIso: nowIso(),
  }));
}

function removeDraftImage(imageId) {
  setDraft((d) => ({
    ...d,
    images: Array.isArray(d.images) ? d.images.filter((item) => item?.id !== imageId) : [],
  }));
}

const ReferenceImagesSection = ({
  editable = false,
  title = t("prescriptions.wizard.referenceImages", "Reference images"),
  subtitle = t(
    "prescriptions.wizard.referenceImagesSubtitle",
    "Keep package or label images attached to this prescription session.",
  ),
}) =>
  uploadedImages.length ? (
    <Card className="p-4">
      <div className="text-label-large text-on-surface-var">{title}</div>
      <div className="mt-1 text-body-medium text-on-surface-var">{subtitle}</div>
      <div className="mt-4 flex gap-3 overflow-x-auto pb-1">
        {uploadedImages.map((item, index) => (
          <div key={item.id || `image-${index}`} className="relative shrink-0">
            <div className="h-24 w-24 overflow-hidden rounded-[14px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.08)]">
              <img
                src={item.src}
                alt={item.file?.name || `${title} ${index + 1}`}
                className="h-full w-full object-cover"
                draggable="false"
              />
            </div>
            {editable ? (
              <button
                type="button"
                aria-label={t("common.delete", "Delete")}
                onClick={() => removeDraftImage(item.id)}
                className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full bg-[rgb(var(--clr-surface-container-low))] shadow-[0_2px_8px_rgba(15,23,42,0.14)]"
              >
                <img src={ICONS.CANCEL} alt="" className="h-4 w-4" draggable="false" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  ) : null;

const currentMedicationRecord = React.useMemo(() => {
  if (!draft?.medication?.name) return null;

  const schedule = {
    frequencyType: draft.schedule?.frequencyType || "",
    everyHours: draft.schedule?.everyHours || 8,
    dosesPerTime: draft.schedule?.dosesPerTime || "1",
    doseUnitLabel: draft.schedule?.doseUnitLabel || currentMedicationTypeLabel || "",
    specificDays: Array.isArray(draft.schedule?.specificDays) ? draft.schedule.specificDays : [],
    cycleUseDays: draft.schedule?.cycleUseDays || 15,
    cyclePauseDays: draft.schedule?.cyclePauseDays || 7,
    startDateIso: draft.schedule?.startDateIso || "",
    endDateIso: draft.schedule?.endDateIso || "",
  };

  return {
    id: draft.medication?.id || `med-${Date.now()}`,
    name: draft.medication?.name || "",
    composition: draft.medication?.composition || "",
    type: draft.medication?.type || "",
    typeLabel: currentMedicationTypeLabel || "",
    strengthValue: String(draft.medication?.strengthValue || "").trim(),
    strengthUnit: draft.medication?.strengthUnit || "mg",
    strengthLabel: [String(draft.medication?.strengthValue || "").trim(), draft.medication?.strengthUnit || "mg"]
      .filter(Boolean)
      .join(" "),
    uses: draft.medication?.uses || "",
    sideEffects: draft.medication?.sideEffects || "",
    imageUrl: draft.medication?.imageUrl || "",
    manufacturer: draft.medication?.manufacturer || "",
    note: String(draft.medication?.note || "").trim(),
    schedule,
    frequencyType: schedule.frequencyType,
    frequencyLabel: selectRxFrequencyLabel({ schedule, t }),
    dosageLabel: buildPrescriptionDosageLabel(
      {
        medication: draft.medication,
        schedule,
        dosesPerTime: schedule.dosesPerTime,
        doseUnitLabel: schedule.doseUnitLabel,
        strengthValue: draft.medication?.strengthValue || "",
        strengthUnit: draft.medication?.strengthUnit || "",
        strengthLabel: [String(draft.medication?.strengthValue || "").trim(), draft.medication?.strengthUnit || "mg"]
          .filter(Boolean)
          .join(" "),
      },
      { t, languageCode },
    ),
    startsOn: schedule.startDateIso || "",
    endsOn: schedule.endDateIso || "",
  };
}, [currentMedicationTypeLabel, draft, languageCode, t]);

function hydrateEditorFromMedication(item) {
  if (!item) return;
  setDraft((d) => ({
    ...d,
    medicationQuery: item.name || "",
    medication: {
      id: item.id || "",
      name: item.name || "",
      composition: item.composition || "",
      type: item.type || "",
      strengthValue: item.strengthValue || "",
      strengthUnit: item.strengthUnit || "mg",
      uses: item.uses || "",
      sideEffects: item.sideEffects || "",
      imageUrl: item.imageUrl || "",
      manufacturer: item.manufacturer || "",
      note: item.note || "",
    },
    schedule: {
      frequencyType: item.schedule?.frequencyType || "",
      everyHours: item.schedule?.everyHours || 8,
      dosesPerTime: item.schedule?.dosesPerTime || "1",
      doseUnitLabel: item.schedule?.doseUnitLabel || item.typeLabel || "Capsule",
      specificDays: Array.isArray(item.schedule?.specificDays) ? item.schedule.specificDays : [],
      cycleUseDays: item.schedule?.cycleUseDays || 15,
      cyclePauseDays: item.schedule?.cyclePauseDays || 7,
      startDateIso: item.schedule?.startDateIso || "",
      endDateIso: item.schedule?.endDateIso || "",
    },
    updatedAtIso: nowIso(),
  }));
}

function saveCurrentMedication() {
  if (!currentMedicationRecord) return;

  setDraft((d) => {
    const existing = Array.isArray(d.medications) ? d.medications : [];
    const nextRecord = {
      ...currentMedicationRecord,
      id: d.medication?.id || currentMedicationRecord.id,
    };
    const existingIndex = existing.findIndex((item) => item.id === nextRecord.id);
    const nextMedications =
      existingIndex >= 0
        ? existing.map((item, index) => (index === existingIndex ? nextRecord : item))
        : [...existing, nextRecord];

    return {
      ...d,
      medications: nextMedications,
      medication: {
        ...(d.medication || {}),
        id: nextRecord.id,
      },
      updatedAtIso: nowIso(),
    };
  });
}

function removeMedicationCard(medicationId) {
  const nextMedications = savedMedications.filter((item) => item.id !== medicationId);
  setDraft((d) => ({
    ...d,
    medications: nextMedications,
    updatedAtIso: nowIso(),
  }));

  if (draft?.medication?.id === medicationId) {
    resetMedicationEditor();
  }
}

function moveMedication(medicationId, direction) {
  const currentIndex = savedMedications.findIndex((item) => item.id === medicationId);
  const nextIndex = currentIndex + direction;
  if (currentIndex < 0 || nextIndex < 0 || nextIndex >= savedMedications.length) return;

  const nextMedications = [...savedMedications];
  const [item] = nextMedications.splice(currentIndex, 1);
  nextMedications.splice(nextIndex, 0, item);

  setDraft((d) => ({
    ...d,
    medications: nextMedications,
    updatedAtIso: nowIso(),
  }));
}

const primaryMedication = savedMedications[0] || currentMedicationRecord || null;

const qrValue = React.useMemo(() => {
  if (!verifyPrescriptionId) return "";

  return buildPrescriptionVerifyUrl(verifyPrescriptionId, window.location.origin);
}, [
  verifyPrescriptionId,
]);
const verifyPath = React.useMemo(
  () => buildPrescriptionVerifyPath(verifyPrescriptionId),
  [verifyPrescriptionId]
);
const verifyState = React.useMemo(
  () =>
    buildPrescriptionVerifyState({
      from: "doctor-rx-wizard",
      actorRole: "doctor",
      prescriptionId: verifyPrescriptionId,
      returnTo: issuedPrescriptionId ? `/appointments/prescriptions/${issuedPrescriptionId}` : location.pathname,
      returnState: issuedPrescriptionId ? buildIssuedPrescriptionState(issuedPrescriptionId) : location.state || null,
      snapshot: buildPrescriptionVerifySnapshot({
        status: issueDone ? "issued" : draft?.status || "draft",
        issuedAt: draft?.updatedAtIso || "",
        expiresAt: primaryMedication?.schedule?.endDateIso || draft?.schedule?.endDateIso || "",
        medicineName: rx.medicineName || "",
        form: rx.form || "",
        ingredients: rx.ingredients || "",
        strengthValue: primaryMedication?.strengthValue || draft?.medication?.strengthValue || "",
        strengthUnit: primaryMedication?.strengthUnit || draft?.medication?.strengthUnit || "",
        strengthLabel: rx.form || "",
        frequencyLabel: primaryMedication?.frequencyLabel || selectRxFrequencyLabel({ schedule: primaryMedication?.schedule || draft?.schedule, t }),
        dosageLabel: primaryMedication?.dosageLabel || dosageLabel,
        startsOn: primaryMedication?.schedule?.startDateIso || draft?.schedule?.startDateIso || "",
        endsOn: primaryMedication?.schedule?.endDateIso || draft?.schedule?.endDateIso || "",
        medications: savedMedications,
        patient: verifyPatient,
        doctor: {
          ...doctor,
          prescriptionName: doctorPrescriptionName || doctor.name || "",
          facility: verifyFacility,
        },
        signature_image: doctor.signatureSrc || "",
      }),
    }),
  [
    buildIssuedPrescriptionState,
    doctor,
    doctorPrescriptionName,
    dosageLabel,
    draft,
    issueDone,
    issuedPrescriptionId,
    location.pathname,
    location.state,
    primaryMedication,
    rx,
    savedMedications,
    verifyFacility,
    verifyPatient,
    verifyPrescriptionId,
  ]
);

React.useEffect(() => {
  let alive = true;
  setQrSrc(null);

  if (!qrValue) {
    return () => {
      alive = false;
    };
  }

  makeBrandedQrDataUrl(qrValue, {
    iconSrc: abs(QR_ELEPHANT),
  })
    .then((url) => {
      if (alive) setQrSrc(url);
    })
    .catch(() => {
      if (alive) setQrSrc(null);
    });

  return () => {
    alive = false;
  };
}, [qrValue]);

// -------------------------
// Sheets: single source of truth
// -------------------------
const [activeSheet, setActiveSheet] = React.useState(null); // "frequency" | "hours" | "days" | null

const [daysDraft, setDaysDraft] = React.useState(() =>
  Array.isArray(draft.schedule?.specificDays) ? draft.schedule.specificDays : []
);

function closeActiveSheet() {
  setActiveSheet(null);
}

function openFrequencySheet() {
  setActiveSheet("frequency");
}

function openHoursSheet() {
  setActiveSheet("hours");
}

function openDaysSheet() {
  setDaysDraft(Array.isArray(draft.schedule?.specificDays) ? draft.schedule.specificDays : []);
  setActiveSheet("days");
}

function stepKey(appointmentId) {
  return `afrimed:rx:step:${String(appointmentId)}`;
}

  const onFilesPicked = async (files, kind) => {
    if (!files || files.length === 0) return;

    const items = await Promise.all(
      Array.from(files).map(async (file) => ({
        id: String(Date.now()) + "-" + Math.random().toString(16).slice(2),
        src: (await readFileAsDataUrl(file)) || URL.createObjectURL(file),
        kind,
        file,
      })),
    );

    setDraft((d) => ({
      ...d,
      images: [...(d.images || []), ...items],
    }));
  };

// Load Medicine_Details.csv once
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setMedLoading(true);
        const normalized = await loadMedicineCatalogue();

        if (alive) setMedRows(normalized);
      } catch {
        if (alive) setMedRows([]);
      } finally {
        if (alive) setMedLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // Persist draft
  React.useEffect(() => {
    saveDraft(draft);
  }, [draft]);

  React.useEffect(() => {
    setDraft((current) => {
      if ((current?.notes || "") === notes) return current;
      return {
        ...current,
        diagnosisNotes: notes,
        notes,
        updatedAtIso: nowIso(),
      };
    });
  }, [notes]);

function clearMedicationSelection() {
    resetMedicationEditor();
  }

  function goBack() {
  if (unitSheetOpen) return setUnitSheetOpen(false);
  if (imagesSheetOpen) return setImagesSheetOpen(false);

  if (step === STEP_SIGN) return setStep(STEP_REVIEW);
  if (step === STEP_REVIEW) return setStep(STEP_BUILDER);
  if (step === STEP_BUILDER) return setStep(0);

  nav(`/appointments/details/${appointmentId}`, { replace: true });
}

  function canContinue() {
    if (step === 0) return Boolean(draft.medication?.name || draft.medicationQuery?.trim());

    if (step === STEP_BUILDER) {
      if (!draft.medication?.name) return false;
      if (!draft.medication?.type) return false;
      if (!String(draft.medication?.strengthValue || "").trim()) return false;
      const f = draft.schedule?.frequencyType;
      if (!f) return false;
      if (f === "daily") return Number(draft.schedule?.everyHours || 0) > 0;
      if (f === "cyclical") {
        return Number(draft.schedule?.cycleUseDays || 0) > 0 && Number(draft.schedule?.cyclePauseDays || 0) >= 0;
      }
      if (f === "specific_days") return (draft.schedule?.specificDays || []).length > 0;
      if (f === "as_needed") return true;
      return false;
    }

    if (step === STEP_REVIEW) return savedMedications.length > 0;
    if (step === STEP_SIGN) return issueDone || signaturePersisted || Boolean(devSigSrc);
    return true;
  }

async function onContinue() {
  if (step === 0) {
    if (!draft.medication?.name && draft.medicationQuery?.trim()) {
      const exactMatch = results.find((item) => normName(item.name) === normName(draft.medicationQuery));
      if (exactMatch) {
        setDraft((d) => ({
          ...d,
          medication: {
            ...d.medication,
            id: d.medication?.id || "",
            name: exactMatch.name,
            composition: exactMatch.composition,
            uses: exactMatch.uses,
            sideEffects: exactMatch.sideEffects,
            imageUrl: exactMatch.imageUrl,
            manufacturer: exactMatch.manufacturer,
          },
        }));
      } else {
        addCustomMedicationFromQuery();
        return;
      }
    }
    return setStep(STEP_BUILDER);
  }
  if (step === STEP_BUILDER) {
    saveCurrentMedication();
    return setStep(STEP_REVIEW);
  }
  if (step === STEP_REVIEW) return setStep(STEP_SIGN);

    if (step === STEP_SIGN) {
      if (issueDone && issuedPrescriptionId) {
        nav(`/appointments/prescriptions/${issuedPrescriptionId}`, {
          replace: true,
          state: buildIssuedPrescriptionState(issuedPrescriptionId),
        });
        return;
      }

      if (issueBusy) return;

      if (!signaturePersisted && !devSigSrc) {
        setIssueErr(
          t(
            "prescriptions.wizard.errors.signatureRequired",
            "Signature required\nAdd and save your signature before issuing this prescription.",
          ),
        );
        return;
      }

      const signature_image =
        (await snapshotSignatureImage(doctor.signatureSrc)) || SIG_PLACEHOLDER;
      const issuedAtIso = nowIso();
      const medicationsToIssue = savedMedications.length
        ? savedMedications
        : currentMedicationRecord
          ? [currentMedicationRecord]
          : [];
      const primaryMedicationToIssue = medicationsToIssue[0] || null;
      const primarySchedule = primaryMedicationToIssue?.schedule || draft.schedule || {};
      const endIso = primarySchedule?.endDateIso
        ? new Date(`${String(primarySchedule.endDateIso).slice(0, 10)}T23:59:59.000Z`).toISOString()
        : null;
      const startIso = primarySchedule?.startDateIso
        ? new Date(`${String(primarySchedule.startDateIso).slice(0, 10)}T00:00:00.000Z`).toISOString()
        : null;

      const payload = {
        rxId,
        appointmentId: String(appointmentId),
        signed_at: issuedAtIso,
        signature_image,
        doctor: {
          name: doctorPrescriptionName,
          fullName: doctor.name,
          regNo: doctor.regNo,
        },
        schedule: primarySchedule,
        medication: primaryMedicationToIssue,
        medications: medicationsToIssue,
        notes,
        status: "issued",
      };

      devIssueSnapshot(payload);
      setIssueErr("");
      setIssueBusy(true);

      (async () => {
        try {
          const { data: sessionRes } = await supabase.auth.getSession();
          const authUserId = sessionRes?.session?.user?.id || null;

          const { data: appt, error: apptErr } = await supabase
            .from("appointments")
            .select("id, doctor_id, patient_id, care_subject_type, care_subject_dependant_id, care_subject_name, care_subject_relationship, care_subject_date_of_birth, guardian_user_id, booked_by_user_id, responsible_user_id, responsible_relationship")
            .eq("id", appointmentId)
            .maybeSingle();

          if (apptErr) throw apptErr;
          if (!appt?.id) {
            throw new Error(t("prescriptions.wizard.errors.appointmentNotFound", "Appointment not found for this prescription."));
          }

          let doctorAuthUserId = authUserId || null;
          let doctorPrescriptionOwnerId = null;
          let patientAuthUserId = null;
          let patientPrescriptionOwnerId = null;

          if (appt.doctor_id) {
            const { data: doctorByProfileId, error: doctorProfileByIdErr } = await supabase
              .from("doctor_profiles")
              .select("id, user_id")
              .eq("id", appt.doctor_id)
              .maybeSingle();

            if (doctorProfileByIdErr) throw doctorProfileByIdErr;

            let doctorByUserId = doctorByProfileId;
            if (!doctorByUserId?.id && !doctorByUserId?.user_id) {
              const { data: doctorByUserFallback, error: doctorByUserErr } = await supabase
                .from("doctor_profiles")
                .select("id, user_id")
                .eq("user_id", appt.doctor_id)
                .maybeSingle();

              if (doctorByUserErr) throw doctorByUserErr;
              doctorByUserId = doctorByUserFallback || null;
            }

            doctorPrescriptionOwnerId =
              doctorByUserId?.id || doctorByProfileId?.id || null;
            doctorAuthUserId =
              doctorByUserId?.user_id ||
              doctorByProfileId?.user_id ||
              appt.doctor_id ||
              authUserId ||
              null;
          }

          if (appt.patient_id) {
            const { data: patientById, error: patientByIdErr } = await supabase
              .from("patients")
              .select("id, user_id")
              .eq("id", appt.patient_id)
              .maybeSingle();

            if (patientByIdErr) throw patientByIdErr;

            let patientByUser = patientById;
            if (!patientByUser?.id && !patientByUser?.user_id) {
              const { data: patientByUserFallback, error: patientByUserErr } = await supabase
                .from("patients")
                .select("id, user_id")
                .eq("user_id", appt.patient_id)
                .maybeSingle();

              if (patientByUserErr) throw patientByUserErr;
              patientByUser = patientByUserFallback || null;
            }

            patientPrescriptionOwnerId =
              patientByUser?.id || patientById?.id || appt.patient_id || null;
            patientAuthUserId =
              patientByUser?.user_id || patientById?.user_id || null;
          }

          if (!doctorPrescriptionOwnerId || !patientPrescriptionOwnerId) {
            throw new Error(
              t(
                "prescriptions.wizard.errors.recordLinkageMissing",
                "Missing doctor/patient record linkage for prescription issue.",
              ),
            );
          }

          if (!doctorAuthUserId || !patientAuthUserId) {
            throw new Error(
              t(
                "prescriptions.wizard.errors.authLinkageMissing",
                "Missing doctor/patient auth linkage for prescription issue.",
              ),
            );
          }

          let guardianContact = null;
          if (
            String(appt?.care_subject_type || "").trim().toLowerCase() === "dependant" &&
            patientAuthUserId
          ) {
            const { data: guardianProfile } = await supabase
              .from("profiles")
              .select("display_name, full_name, first_name, last_name, email, phone")
              .eq("user_id", patientAuthUserId)
              .maybeSingle();
            guardianContact = guardianProfile || null;
          }

          const prescriptionPatientName =
            firstText(appt?.care_subject_name, verifyPatient.name) || "";
          const prescriptionPatientDob =
            firstText(appt?.care_subject_date_of_birth, verifyPatient.dob) || "";
          const guardianName =
            firstText(
              guardianContact?.display_name,
              guardianContact?.full_name,
              [guardianContact?.first_name, guardianContact?.last_name].filter(Boolean).join(" "),
              location.state?.restored?.guardianName,
              String(appt?.care_subject_type || "").trim().toLowerCase() === "dependant"
                ? "Responsible adult"
                : prescriptionPatientName,
            ) || "";
          const guardianEmail =
            firstText(guardianContact?.email, verifyPatient.email) || "";
          const guardianPhone =
            firstText(guardianContact?.phone, verifyPatient.phone) || "";

          const issuedDoctorDisplayName = selectDoctorDisplayName(
            {
              doctorName: doctor.name,
              full_name: doctor.name,
            },
            doctor,
          );
          const issuedDoctorPrescriptionName =
            selectDoctorPrescriptionName(
              {
                doctorName: doctor.name,
                doctorFullName: doctor.name,
              },
              doctor,
            ) || doctorPrescriptionName;
          const issuedDoctorLicense = selectDoctorLicenseNumber(
            {
              regNo: doctor.regNo,
            },
            doctor,
          );

          const meta = {
            medicineName: primaryMedicationToIssue?.name || "",
            medicineType: primaryMedicationToIssue?.type || "",
            form: primaryMedicationToIssue?.typeLabel || "",
            strengthValue: primaryMedicationToIssue?.strengthValue || "",
            strengthUnit: primaryMedicationToIssue?.strengthUnit || "",
            strengthLabel: primaryMedicationToIssue?.strengthLabel || "",
            composition: primaryMedicationToIssue?.composition || "",
            ingredients: primaryMedicationToIssue?.composition || "",
            uses: primaryMedicationToIssue?.uses || "",
            sideEffects: primaryMedicationToIssue?.sideEffects || "",
            imageUrl: primaryMedicationToIssue?.imageUrl || "",
            manufacturer: primaryMedicationToIssue?.manufacturer || "",
            frequencyType: primaryMedicationToIssue?.frequencyType || "",
            frequencyLabel: primaryMedicationToIssue?.frequencyLabel || selectRxFrequencyLabel({ schedule: primarySchedule, t }),
            dosageLabel: primaryMedicationToIssue?.dosageLabel || dosageLabel,
            dosesPerTime: primarySchedule?.dosesPerTime || "",
            doseUnitLabel: primarySchedule?.doseUnitLabel || "",
            everyHours: primarySchedule?.everyHours || null,
            specificDays: Array.isArray(primarySchedule?.specificDays) ? primarySchedule.specificDays : [],
            cycleUseDays: primarySchedule?.cycleUseDays || null,
            cyclePauseDays: primarySchedule?.cyclePauseDays || null,
            startsOn: startIso,
            endsOn: endIso,
            medications: medicationsToIssue.map((item, index) => ({
              id: item.id || `med-${index + 1}`,
              name: item.name || "",
              composition: item.composition || "",
              type: item.type || "",
              form: item.typeLabel || "",
              strengthValue: item.strengthValue || "",
              strengthUnit: item.strengthUnit || "",
              strengthLabel: item.strengthLabel || "",
              note: item.note || "",
              frequencyType: item.frequencyType || "",
              frequencyLabel: item.frequencyLabel || "",
              dosageLabel: item.dosageLabel || "",
              schedule: item.schedule || {},
              startsOn: item.startsOn || "",
              endsOn: item.endsOn || "",
            })),
            medicationCount: medicationsToIssue.length,
            doctorName: issuedDoctorPrescriptionName || "",
            doctorFullName: issuedDoctorDisplayName || "",
            regNo: issuedDoctorLicense || "",
            registerNumber: issuedDoctorLicense || "",
            doctorLicenseNumber: issuedDoctorLicense || "",
            doctorProfileId: doctorPrescriptionOwnerId || "",
            doctorAuthUserId: doctorAuthUserId || "",
            patientRecordId: patientPrescriptionOwnerId || "",
            patientAuthUserId: patientAuthUserId || "",
            patientName: prescriptionPatientName,
            patientPhone: guardianPhone,
            patientEmail: guardianEmail,
            patientAddress: verifyPatient.address || "",
            patientDob: prescriptionPatientDob,
            patientNationalId: verifyPatient.nationalId || "",
            careSubjectType:
              String(appt?.care_subject_type || "").trim().toLowerCase() === "dependant"
                ? "dependant"
                : "self",
            dependantId: appt?.care_subject_dependant_id || "",
            guardianUserId: appt?.guardian_user_id || patientAuthUserId || "",
            guardianName,
            guardianEmail,
            guardianPhone,
            responsibleUserId:
              appt?.responsible_user_id || appt?.guardian_user_id || patientAuthUserId || "",
            responsibleRelationship:
              appt?.responsible_relationship ||
              appt?.care_subject_relationship ||
              "Self",
            diagnosisTitle: draft?.diagnosisTitle || "",
            diagnosisBody: notes || "",
            facilityName: verifyFacility.name || "",
            facilityAddress: verifyFacility.address || "",
            facilityPostalCode: verifyFacility.postalCode || "",
            facilityCity: verifyFacility.city || "",
            facilityCountry: verifyFacility.country || "",
            facilityPhone: verifyFacility.phone || "",
            facilityEmail: verifyFacility.email || "",
            issuedVia: "AfriMed™",
          };

          if (clinicalActionId) {
            const reviewed = await loadClinicalAction(clinicalActionId);
            if (reviewed.appointment_id !== appointmentId || reviewed.kind !== 'prescription' || reviewed.state !== 'reviewed') throw new Error(t('clinical.error'));
            meta.clinical_action_id = clinicalActionId;
          }
          const insertPayload = {
            appointment_id: appt.id,
            status: "issued",
            issued_at: issuedAtIso,
            signed_at: issuedAtIso,
            expires_at: endIso,
            diagnosis: draft?.diagnosisTitle || draft?.diagnosis || null,
            notes: notes || null,
            signature_image,
            digital_seal: `afrimed:${String(appt.id)}:${Date.now()}`,
            meta,
          };

          const doctorInsertCandidates = uniqueIds(doctorAuthUserId);
          const patientInsertCandidates = uniqueIds(patientAuthUserId);

          let created = null;
          let createErr = null;

          outerInsertLoop:
          for (const doctorInsertId of doctorInsertCandidates) {
            for (const patientInsertId of patientInsertCandidates) {
              const { data, error } = await supabase
                .from("prescriptions")
                .insert({
                  ...insertPayload,
                  doctor_user_id: doctorInsertId,
                  patient_user_id: patientInsertId,
                })
                .select("id, external_rx_id")
                .single();

              if (!error && data?.id) {
                created = data;
                createErr = null;
                break outerInsertLoop;
              }

              createErr = error || createErr;

              const isDoctorFkError = String(error?.message || "").includes(
                "prescriptions_doctor_user_id_fkey",
              );
              const isPatientFkError = String(error?.message || "").includes(
                "prescriptions_patient_user_id_fkey",
              );

              if (!isDoctorFkError && !isPatientFkError) {
                break outerInsertLoop;
              }
            }
          }

          if (createErr) throw createErr;
          if (!created?.id) {
            throw new Error(
              t(
                "prescriptions.wizard.errors.idMissing",
                "Prescription issued, but no ID was returned.",
              ),
            );
          }

          const issuedRxReference = String(created.external_rx_id || "").trim();

          if (issuedRxReference) {
            const { error: referenceMetaError } = await supabase
              .from("prescriptions")
              .update({
                meta: {
                  ...meta,
                  rxNo: issuedRxReference,
                },
              })
              .eq("id", String(created.id));

            if (referenceMetaError) {
              console.warn(
                "[DoctorPrescriptionWizard] prescription reference meta sync skipped:",
                referenceMetaError?.message || referenceMetaError,
              );
            }
          }

          setDraft((d) => ({
            ...d,
            id: created.id,
            prescriptionId: created.id,
            rxNo: issuedRxReference || d?.rxNo || "",
            status: "issued",
            updatedAtIso: nowIso(),
            doctor: {
              ...(d.doctor || {}),
              id: doctorAuthUserId || doctorPrescriptionOwnerId,
              signatureSrc: signature_image,
            },
            patient: {
              ...(d.patient || {}),
              id: patientAuthUserId || patientPrescriptionOwnerId,
            },
            supportConversationId: "",
          }));
          setIssuedPrescriptionId(String(created.id));
          setIssueDone(true);
          setQrOpen(true);
        } catch (e) {
          setIssueErr(e?.message || t("prescriptions.wizard.errors.issueFailed", "Failed to issue prescription."));
        } finally {
          setIssueBusy(false);
        }
      })();

      return;
    }
}

function onBack() {
  if (step === STEP_SIGN) return setStep(STEP_REVIEW);
  if (step === STEP_REVIEW) return setStep(STEP_BUILDER);
  if (step === STEP_BUILDER) return setStep(0);
  // default fallback
  return setStep(0);
}

function addCustomMedicationFromQuery() {
  const raw = (draft.medicationQuery || "").trim();
  if (!raw) return;

  const pretty = raw.replace(/\s+/g, " ").trim();

  // Set a "custom" medication object that behaves like a selected result
  setDraft((d) => ({
  ...d,
  medicationQuery: pretty,
  medication: {
    ...(d.medication || {}),
    id: d.medication?.id || "",
    name: pretty,
    // keep safe blanks; your later steps already handle missing composition/ingredients
    composition: "",
    ingredients: "",
    isCustom: true,
    note: d.medication?.note || "",
  },
}));

  // Move forward like "med selected" would
  setStep(STEP_BUILDER);
}

// Results from CSV
  const results = React.useMemo(() => {
    const q = normName(draft.medicationQuery);
    if (!q) return [];
    if (!medRows.length) return [];
    return medRows.filter((r) => r.nameNorm.includes(q)).slice(0, 80);
  }, [draft.medicationQuery, medRows]);

// -------------------------
// Header (wizard-only)
// Step0: X close + title/subtitle (RxWizardTopBar)
// Step2: true centered title + SaveChip (TopBarCenter)
// Other: back + title/subtitle (RxWizardTopBar)
// -------------------------
const headerTitle =
  step === 0
    ? t("prescriptions.wizard.header.addPrescription", "Add a prescription")
    : step === STEP_BUILDER
      ? (draft.medication?.name || t("prescriptions.wizard.medication", "Medication"))
      : step === STEP_REVIEW
        ? t("prescriptions.wizard.reviewPrescription", "Review prescription")
        : t("prescriptions.wizard.signAndPrescribe", "Sign & Prescribe");

const headerSubtitle =
  step === 0
    ? t(
        "prescriptions.wizard.header.subtitle",
        "You can add it manually or use your camera to attach it.",
      )
    : step === STEP_BUILDER
      ? [rxTypeLabel || "", draft.medication?.composition || ""].filter(Boolean).join(" • ")
      : step === STEP_REVIEW
        ? t("prescriptions.wizard.reviewSubtitle", "{count} medicine(s) ready to review.", {
            count: savedMedications.length,
          })
        : t("prescriptions.wizard.signSubtitle", "Confirm the prescription summary, then sign once before issue.");

const isEntryStep = step === 0;

const Header = (
  <TopBarRx
    title={headerTitle}
    subtitle={headerSubtitle}
    onBack={goBack}
    close={isEntryStep}
    right={
      step === STEP_SIGN ? (
        <ActionIconButton
          icon={ICON_QR}
          label={t("prescriptions.wizard.qrCode", "QR code")}
          onClick={() => {
            if (!issueDone || !issuedPrescriptionId) {
              setIssueErr(
                t(
                  "prescriptions.wizard.issueFirstForQr",
                  "Issue prescription first to activate the verifiable QR.",
                ),
              );
              return;
            }
            setIssueErr("");
            setQrOpen(true);
          }}
        />
      ) : null
    }
    margin="mb-0"
  />
);

const showCover = step === 0 || step === STEP_BUILDER || step === STEP_REVIEW || step === STEP_SIGN;

// -------------------------
// Step 0: Add prescription
// -------------------------
const Step0 = (
  <div className="w-full"> 
    <div className="mt-0">        
      {/* keep semantics for accessibility, but don't show the extra heading */}
      <label htmlFor="rx-medication-search" className="sr-only">
        {t("prescriptions.wizard.medication", "Medication")}
      </label>

      <div className="h-14 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.06)] px-4 flex items-center gap-3">
        <img
          src={ICONS.SEARCH}
          alt=""
          className="w-6 h-6"
          draggable="false"
          style={
            isDark
              ? { filter: "brightness(0) saturate(100%) invert(80%) sepia(8%) saturate(348%) hue-rotate(182deg) brightness(89%) contrast(87%)" }
              : undefined
          }
        />

        <input
          id="rx-medication-search"
          value={draft.medicationQuery}
          onChange={(e) => {
            const nextQ = e.target.value;

            setDraft((d) => {
              const hadSelection = Boolean(d.medication?.name);
              return {
                ...d,
                medicationQuery: nextQ,
                medication: hadSelection
                  ? {
                      ...d.medication,
                      name: "",
                      composition: "",
                      uses: "",
                      sideEffects: "",
                      imageUrl: "",
                      manufacturer: "",
                      type: "",
                      strengthValue: "",
                      strengthUnit: "mg",
                    }
                  : d.medication,
                schedule: hadSelection
                  ? {
                      ...d.schedule,
                      frequencyType: "",
                      specificDays: [],
                    }
                  : d.schedule,
              };
            });
          }}
          placeholder={t("prescriptions.wizard.medication", "Medication")}
          className="flex-1 bg-transparent outline-none text-body-large text-on-surface"
        />

        <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); addCustomMedicationFromQuery(); }}
        className="shrink-0 grid place-items-center rounded-full
                    w-8 h-8 border border-[rgba(24,50,119,0.16)]
                    hover:bg-[rgba(24,50,119,0.04)]
                    focus:outline-none focus:ring-2 focus:ring-[rgba(24,50,119,0.24)]"
        aria-label={t("prescriptions.wizard.addMedication", "Add medication")}
        >
        <img src={ICONS.ADD_TWO} alt="" className="h-5 w-5" draggable="false" />
        </button>

        {/* your clear-in-input stays (you already fixed this) */}
        {!!draft.medicationQuery && (
          <button
            type="button"
            aria-label={t("common.actions.clear", "Clear")}
            onClick={() => setDraft((d) => ({ ...d, medicationQuery: "" }))}
            className="h-10 w-10 flex items-center justify-center"
          >
            <img src={ICONS.CANCEL} alt="" className="w-5 h-5" draggable="false" />
          </button>
        )}
      </div>

      {/* Tonal Upload button (your blueprint one) */}
      <button
        type="button"
        onClick={() => setImagesSheetOpen(true)}
        className="mt-3 rounded-xl bg-[rgba(77,163,255,0.12)] px-4 py-3 inline-flex items-center gap-2"
      >
        <span className="inline-flex h-4 w-4 items-center justify-center text-primary">
          <span className="text-body-large">＋</span>
        </span>
        <span className="text-primary text-label-large">
          {t("prescriptions.wizard.uploadImage", "Upload image")}
        </span>
      </button>
    </div>
  </div>
);

// -------------------------
// Step 2: Medication search (scroll-safe, blueprint header + divider)
// -------------------------
const Step2 = (
  <div className="w-full flex flex-col min-h-0">
    {/* Search row */}
    <div className="h-14 bg-transparent flex items-center gap-3">
    <img
        src={ICONS.SEARCH}
        alt=""
        className="w-6 h-6"
        draggable="false"
        style={
          isDark
            ? { filter: "brightness(0) saturate(100%) invert(80%) sepia(8%) saturate(348%) hue-rotate(182deg) brightness(89%) contrast(87%)" }
            : undefined
        }
      />

    <input
        value={draft.medicationQuery}
        onChange={(e) => {
        const nextQ = e.target.value;
        setDraft((d) => ({ ...d, medicationQuery: nextQ }));
        }}
        placeholder={t("prescriptions.wizard.searchOrAdd", "Search or add")}
        inputMode="search"
        className="flex-1 bg-transparent outline-none text-body-large text-on-surface"
    />

    {!!draft.medicationQuery && (
        <button
        type="button"
        aria-label={t("common.actions.clearSearch", "Clear search")}
        onClick={() => setDraft((d) => ({ ...d, medicationQuery: "" }))}
        className="h-10 w-10 flex items-center justify-center"
        >
        <img src={ICONS.CANCEL} alt="" className="w-5 h-5" draggable="false" />
        </button>
    )}

    <button
        type="button"
        aria-label={t("prescriptions.wizard.addMedication", "Add medication")}
        onClick={() => addCustomMedicationFromQuery()}
        className="shrink-0 grid place-items-center rounded-full
                    w-8 h-8 border border-[rgba(24,50,119,0.16)]
                    hover:bg-[rgba(24,50,119,0.04)]
                    focus:outline-none focus:ring-2 focus:ring-[rgba(24,50,119,0.24)]"
    >
        <img src={ICONS.ADD_TWO} alt="" className="w-5 h-5" draggable="false" />
    </button>
    </div>

    {/* Full bleed divider like blueprint */}
    <div className="mt-4 -mx-4 sm:-mx-6">
      <Divider variant="outline" full />
    </div>

    <div className="mt-4 text-body-medium text-on-surface-var">
      {t("prescriptions.wizard.possibleMatches", "Possible matches")}
    </div>

    {/* Scroll container */}
    <div className="mt-3 flex-1 min-h-0">
      <Card className="h-full overflow-auto overscroll-contain">
        {medLoading ? (
          <div className="px-4 py-4 text-body-medium text-on-surface-var">
            {t("prescriptions.wizard.loadingMatches", "Loading… one moment please!")}
          </div>
        ) : results.length === 0 ? (
            <div className="px-4 py-4 text-body-medium text-on-surface-var">
            {t(
              "prescriptions.wizard.noMatchesAdd",
              "No matches. Tap + to add “{query}”.",
              { query: (draft.medicationQuery || "").trim() },
            )}
            </div>
            ) : (
          results.map((r, idx) => {
            const active = draft.medication?.name === r.name;
            return (
              <div key={`${r.name}-${idx}`}>
	                <button
	                  type="button"
	                  className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left"
	                  onClick={() =>
	                    setDraft((d) => {
	                      const inferredStrength = extractMedicationStrength(r.composition, r.name);

	                      return {
	                        ...d,
	                        medication: {
	                          ...d.medication,
	                          name: r.name,
	                          composition: r.composition,
	                          uses: r.uses,
	                          sideEffects: r.sideEffects,
	                          imageUrl: r.imageUrl,
	                          manufacturer: r.manufacturer,
	                          strengthValue:
	                            String(d?.medication?.strengthValue || "").trim() ||
	                            inferredStrength?.value ||
	                            "",
	                          strengthUnit:
	                            String(d?.medication?.strengthUnit || "").trim() ||
	                            inferredStrength?.unit ||
	                            d?.medication?.strengthUnit ||
	                            "mg",
	                        },
	                      };
	                    })
	                  }
	                >
                  <div className="min-w-0">
                    <div className="text-body-medium text-on-surface" style={truncateOneLineStyle()}>
                      {r.name}
                    </div>
                    <div className="text-body-medium text-on-surface-var" style={truncateOneLineStyle()}>
                      {r.composition}
                    </div>
                  </div>
                  <Radio active={active} />
                </button>
                {idx !== results.length - 1 ? <Divider inset variant="outlineVariant" /> : null}
              </div>
            );
          })
        )}
      </Card>
    </div>
  </div>
);

// -------------------------
// Step 3: Choose medication type
// -------------------------
const Step3 = (
    <div className="w-full">
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.chooseMedicationType", "Choose medication type")}
      </div>

      <div className="mt-2">
        <Card>
          {medTypeOptions.map((option, idx) => {
            const active = draft.medication?.type === option.key;
            return (
              <div key={option.key}>
                <button
                  type="button"
                  className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left"
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      medication: { ...d.medication, type: option.key },
                      schedule: { ...d.schedule, doseUnitLabel: option.label },
                    }))
                  }
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <img src={option.icon} alt="" className="w-6 h-6" draggable="false" />
                    <div className="text-body-large text-on-surface">{option.label}</div>
                  </div>
                  <Radio active={active} />
                </button>
                {idx !== medTypeOptions.length - 1 ? <Divider inset variant="outlineVariant" /> : null}
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );

// -------------------------
// Step 4: Strength + unit picker
// -------------------------
const Step4 = (
  <div className="w-full">
    <div className="text-label-large text-on-surface-var text-center">
      {t("prescriptions.wizard.addStrength", "Add the medication strength")}
    </div>

    {/* Strength value + clear */}
    <div className="mt-3 relative flex items-center justify-center">
      <input
        value={draft.medication?.strengthValue || ""}
        onChange={(e) =>
          setDraft((d) => ({
            ...d,
            medication: { ...d.medication, strengthValue: e.target.value },
          }))
        }
        inputMode="decimal"
        placeholder="1.0"
        className="w-full text-center bg-transparent outline-none text-display-large text-on-surface"
      />

      {/* Clear strength (only clears strengthValue) */}
      {draft.medication?.strengthValue ? (
        <button
          type="button"
          aria-label={t("common.actions.clear", "Clear")}
          onClick={() =>
            setDraft((d) => ({
              ...d,
              medication: { ...d.medication, strengthValue: "" },
            }))
          }
          className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center"
        >
          <img src={ICONS.CANCEL} alt="" className="w-6 h-6" draggable="false" />
        </button>
      ) : null}
    </div>

    <div className="mt-3">
      <div className="text-body-medium text-on-surface-var mb-2">
        {t("prescriptions.wizard.chooseUnit", "Choose Unit")}
      </div>

      <button
        type="button"
        onClick={() => {
          // prep defaults, then go to full-screen Choose Unit
          setUnitDraft(draft.medication?.strengthUnit || "mg");
          setUnitSearch("");
          setStep(STEP_UNIT);
        }}
        className="w-full h-14 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.06)] px-4 flex items-center justify-between"
      >
        <div className="text-body-large text-on-surface">
          {draft.medication?.strengthUnit || "mg"}
        </div>
        <img src={ICONS.NAVIGATE_NEXT} alt="" className="w-6 h-6" draggable="false" />
      </button>
    </div>
  </div>
);

// -------------------------
// STEP_UNIT: Choose Unit (full screen, NO cover)
// - TopBarCenter + SaveChip
// - Ghost search row (no pill)
// - Full-bleed dividers
// - Save auto-navigates to Step 5 (Frequency)
// -------------------------
const unitList = React.useMemo(() => {
    const q = normName(unitSearch);
    if (!q) return UNITS;
    return UNITS.filter((u) => normName(u).includes(q));
  }, [unitSearch]);

const StepChooseUnit = (
  <div className="w-full">
    <TopBarCenter
        title={t("prescriptions.wizard.chooseUnit", "Choose Unit")}
        onBack={() => setStep(STEP_BUILDER)}
        right={
            <SaveChip
            disabled={!unitDraft}
            onClick={() => {
                setDraft((d) => ({
                ...d,
                medication: { ...d.medication, strengthUnit: unitDraft || "mg" },
                }));
                setStep(STEP_BUILDER);
            }}
            >
            {t("common.actions.save", "Save")}
        </SaveChip>
      }
    />

    {/* Ghost search row (on background) */}
    <div className="mt-3 h-14 bg-transparent flex items-center gap-3">
      <img
        src={ICONS.SEARCH}
        alt=""
        className="w-6 h-6"
        draggable="false"
        style={
          isDark
            ? { filter: "brightness(0) saturate(100%) invert(80%) sepia(8%) saturate(348%) hue-rotate(182deg) brightness(89%) contrast(87%)" }
            : undefined
        }
      />
      <input
        value={unitSearch}
        onChange={(e) => setUnitSearch(e.target.value)}
        placeholder={t("common.search", "Search")}
        className="flex-1 bg-transparent outline-none text-body-large text-on-surface"
      />
      {unitSearch ? (
        <button
          type="button"
          className="h-9 w-9 flex items-center justify-center"
          aria-label={t("common.actions.clearSearch", "Clear search")}
          onClick={() => setUnitSearch("")}
        >
          <img src={ICONS.CANCEL} alt="" className="w-6 h-6" draggable="false" />
        </button>
      ) : (
        <div className="h-9 w-9" />
      )}
    </div>

    {/* Full-bleed divider like Step 2 blueprint */}
    <div className="mt-2 -mx-4 sm:-mx-6">
      <Divider variant="outlineVariant" />
    </div>

    {/* Card sits below; add a little air so it’s not tight */}
    <div className="mt-7">
      <Card>
        {unitList.map((u, idx) => {
          const active = (unitDraft || "mg") === u;
          return (
            <div key={u}>
              <button
                type="button"
                className="w-full px-4 py-4 flex items-center justify-between text-left"
                onClick={() => setUnitDraft(u)}
              >
                <div className="text-body-large text-on-surface">{u}</div>
                <Radio active={active} />
              </button>

              {idx !== unitList.length - 1 ? (
                <Divider inset variant="outlineVariant" />
              ) : null}
            </div>
          );
        })}
      </Card>
    </div>
  </div>
);

// -------------------------
// Step 5: Frequency (screen)
// - Single row opens Frequency sheet
// -------------------------
const freqLabel =
  draft.schedule?.frequencyType === "daily"
    ? t("prescriptions.wizard.frequency.daily", "Daily")
    : draft.schedule?.frequencyType === "cyclical"
    ? t("prescriptions.wizard.frequency.cyclical", "Cyclical schedule")
    : draft.schedule?.frequencyType === "specific_days"
    ? t("prescriptions.wizard.frequency.specificDays", "On a specific day of the week")
    : draft.schedule?.frequencyType === "as_needed"
    ? t("prescriptions.wizard.frequency.asNeeded", "As needed")
    : t("prescriptions.wizard.select", "Select");

const Step5 = (
  <div className="w-full space-y-3">
    <div className="text-label-large text-on-surface-var">
      {t("prescriptions.wizard.frequency.title", "Frequency")}
    </div>

    <Card className="overflow-hidden">
      <button
        type="button"
        className="w-full text-left"
        onClick={openFrequencySheet}
        onPointerUp={openFrequencySheet} // iOS “tap insurance”
      >
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={ICONS.FREQUENCY} alt="" className="w-6 h-6" draggable="false" />
            <div className="text-body-large text-on-surface min-w-0" style={truncateOneLineStyle()}>
              {t("prescriptions.wizard.frequency.title", "Frequency")}
            </div>
          </div>

          <div className="flex items-center gap-2 min-w-0">
            <div className="text-body-large text-primary" style={truncateOneLineStyle()}>
              {freqLabel}
            </div>
            <img src={ICONS.ELLIPSIS_VERTICAL} alt="" className="w-6 h-6" draggable="false" />
          </div>
        </div>
      </button>
    </Card>
  </div>
);

// -------------------------
// Step 6: Frequency config
// - Hours are selectable (sheet) — no hard-locked "12"
// - Cycle inputs are native number inputs
// - Dates: whole row clickable via invisible <input type="date">
// -------------------------
function Row({ left, right, iconSrc }) {
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {iconSrc ? <img src={iconSrc} alt="" className="w-6 h-6" draggable="false" /> : null}
        <div className="text-body-large text-on-surface min-w-0" style={truncateOneLineStyle()}>
          {left}
        </div>
      </div>
      <div className="text-body-large text-primary" style={truncateOneLineStyle()}>
        {right}
      </div>
    </div>
  );
}

function ClickRow({ left, right, iconSrc, onClick, trailingIconSrc }) {
  // ✅ keep your desired default behaviour:
  const icon =
    trailingIconSrc === undefined ? ICONS.ELLIPSIS_VERTICAL : trailingIconSrc;

  const isEllipsis = icon === ICONS.ELLIPSIS_VERTICAL;

  return (
    <button type="button" className="w-full text-left" onClick={onClick}>
      <div className="px-4 py-3 flex items-center gap-3">
        {/* LEFT */}
        <div className="flex items-center gap-3 min-w-0">
          {iconSrc ? (
            <img src={iconSrc} alt="" className="w-6 h-6" draggable="false" />
          ) : null}
          <div
            className="text-body-large text-on-surface min-w-0"
            style={truncateOneLineStyle()}
          >
            {left}
          </div>
        </div>

        {/* RIGHT */}
        <div className="ml-auto flex items-center justify-end gap-2 min-w-0">
          <div
            className="text-body-large text-primary min-w-0"
            style={truncateOneLineStyle()}
          >
            {right}
          </div>

          {/* Icon rail (same size as chevron), ellipsis nudged to match X */}
          {icon ? (
            <img
              src={icon}
              alt=""
              draggable="false"
              className={
                "w-6 h-6 shrink-0 " + (isEllipsis ? "translate-x-[9px]" : "")
              }
            />
          ) : null}
        </div>
      </div>
    </button>
  );
}

function DateRow({ label, value, onChange }) {
  const inputRef = React.useRef(null);
  const pendingRef = React.useRef(value ? String(value).slice(0, 10) : "");

  React.useEffect(() => {
    const iso = value ? String(value).slice(0, 10) : "";
    pendingRef.current = iso;
    if (inputRef.current && inputRef.current.value !== iso) {
      inputRef.current.value = iso;
    }
  }, [value]);

  const iso = value ? String(value).slice(0, 10) : "";
  const display = iso ? formatDateDisplay(iso) : t("prescriptions.wizard.select", "Select");

  const openPicker = () => {
    const el = inputRef.current;
    if (!el) return;
    try {
      if (typeof el.showPicker === "function") el.showPicker();
      else {
        el.focus();
        el.click();
      }
    } catch {
      el.focus();
      el.click();
    }
  };

  return (
    <div
      className="px-4 py-3 flex items-center justify-between gap-3"
      role="button"
      tabIndex={0}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") openPicker();
      }}
    >
      <div className="text-body-large text-on-surface">{label}</div>

      <div className="relative flex items-center justify-end min-w-0">
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {display}
        </div>

        {/* invisible input over the right side */}
        <input
          ref={inputRef}
          type="date"
          defaultValue={iso}
          className="absolute inset-0 opacity-0 cursor-pointer"
          style={{ width: "100%", height: "100%" }}
          onChange={(e) => {
            const v = e.target.value; // YYYY-MM-DD
            pendingRef.current = v;
            onChange?.(v);
          }}
        />
      </div>
    </div>
  );
}

function selectFrequencyRowLabel(d) {
  const f = d?.schedule?.frequencyType;

  if (f === "daily") return t("prescriptions.wizard.frequency.daily", "Daily");
  if (f === "cyclical") return t("prescriptions.wizard.frequency.cyclical", "Cyclical schedule");
  if (f === "as_needed") return t("prescriptions.wizard.frequency.asNeeded", "As needed");

  if (f === "specific_days") return t("prescriptions.wizard.frequency.specificDays", "On a specific day of the week");

  return t("prescriptions.wizard.select", "Select");
}

const Step6 = (() => {
  const f = draft.schedule?.frequencyType;

  const selected = new Set(draft.schedule?.specificDays || []);
  const selectedLine = Array.from(selected).join(", ");

  const startVal = draft.schedule?.startDateIso ? String(draft.schedule.startDateIso).slice(0, 10) : "";
  const endVal = draft.schedule?.endDateIso ? String(draft.schedule.endDateIso).slice(0, 10) : "";

  // Hours display (never "hard locks"; if empty we display 12 as a sensible fallback)
  const hoursValNum = Number(draft.schedule?.everyHours || 0);
  const hoursDisplay =
    hoursValNum > 0
      ? t("prescriptions.wizard.hoursValue", "{count} hours", { count: hoursValNum })
      : t("prescriptions.wizard.select", "Select");

  // Doses display
  const doseUnit = draft.schedule?.doseUnitLabel || t("prescriptions.wizard.dose", "Dose");

  if (f === "as_needed") {
    return (
      <div className="w-full space-y-3">
        <div className="text-label-large text-on-surface-var">
          {t("prescriptions.wizard.frequency.title", "Frequency")}
        </div>
        <Card>
          <ClickRow
            left={t("prescriptions.wizard.frequency.title", "Frequency")}
            right={selectRxFrequencyLabel({ schedule: draft?.schedule, t })}
            iconSrc={ICONS.FREQUENCY}
            rightIconSrc={ICONS.ELLIPSIS_VERTICAL}
            onClick={openFrequencySheet}
            />
        </Card>
      </div>
    );
  }

if (f === "specific_days") {
  const selectedDays = Array.isArray(draft.schedule?.specificDays) ? draft.schedule.specificDays : [];

  // show nice labels in summary (not raw keys) if you store keys like "mon"
  const daysSummary =
    selectedDays.length
      ? dayOptions.filter((d) => selectedDays.includes(d.key)).map((d) => d.label).join(", ")
      : t("prescriptions.wizard.select", "Select");

  return (
    <div className="w-full space-y-3">
      {/* Frequency */}
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.frequency.title", "Frequency")}
      </div>
      <Card>
        <ClickRow
        left={t("prescriptions.wizard.frequency.title", "Frequency")}
        right={freqLabel}
        iconSrc={ICONS.FREQUENCY}
        trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
        onClick={openFrequencySheet}
        />

        <Divider inset variant="outlineVariant" />

        <ClickRow
        left={t("prescriptions.wizard.every", "Every")}
        right={daysSummary}
        trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
        onClick={openDaysSheet}
        />
      </Card>

      {/* Time & Doses */}
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.timeAndDoses", "Time & Doses")}
      </div>
      <Card>
        <Row
          left={t("prescriptions.wizard.time", "Time")}
          right={t("prescriptions.wizard.cyclicalShort", "Cyclical")}
          iconSrc={ICONS.TIME}
        />
        <Divider inset variant="outlineVariant" />

        {/* ✅ Hours interval lives here */}
        <ClickRow
        left={t("prescriptions.wizard.every", "Every")}
        right={hoursDisplay}
        trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
        onClick={openHoursSheet}
        />

        <Divider inset variant="outlineVariant" />

        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-body-large text-on-surface">{t("prescriptions.wizard.doses", "Doses")}</div>
          <div className="text-body-large text-primary flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={draft.schedule?.dosesPerTime ?? "1"}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  schedule: { ...d.schedule, dosesPerTime: e.target.value },
                }))
              }
              className="w-10 text-right bg-transparent outline-none text-body-large text-primary"
            />
            <span>{doseUnit}</span>
          </div>
        </div>
      </Card>

    {/* Duration */}
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.duration", "Duration")}
      </div>
      <Card>
        <ClickRow
          left={t("prescriptions.wizard.startDate", "Start date")}
          right={
            draft.schedule?.startDateIso
              ? formatDateDisplay(String(draft.schedule.startDateIso).slice(0, 10))
              : t("prescriptions.wizard.select", "Select")
          }
          onClick={openStartDatePicker}
          trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
        />

        <Divider inset variant="outlineVariant" />

        <ClickRow
          left={t("prescriptions.wizard.endDate", "End date")}
          right={
            draft.schedule?.endDateIso
              ? formatDateDisplay(String(draft.schedule.endDateIso).slice(0, 10))
              : t("prescriptions.wizard.select", "Select")
          }
          onClick={openEndDatePicker}
          trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
        />
      </Card>
    </div>
  );
}

if (f === "cyclical") {
  return (
    <div className="w-full space-y-3">
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.frequency.title", "Frequency")}
      </div>
      <Card>
        <ClickRow
        left={t("prescriptions.wizard.frequency.title", "Frequency")}
        right={selectRxFrequencyLabel({ schedule: draft?.schedule, t })}
        iconSrc={ICONS.FREQUENCY}
        rightIconSrc={ICONS.ELLIPSIS_VERTICAL}
        onClick={openFrequencySheet}
        />
        <Divider inset variant="outlineVariant" />
        <Row left={t("prescriptions.wizard.every", "Every")} right={t("prescriptions.wizard.day", "Day")} />
      </Card>

      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.timeAndDoses", "Time & Doses")}
      </div>
      <Card>
        <Row left={t("prescriptions.wizard.time", "Time")} right={t("prescriptions.wizard.cyclicalShort", "Cyclical")} iconSrc={ICONS.TIME} />
        <Divider inset variant="outlineVariant" />

        <ClickRow left={t("prescriptions.wizard.every", "Every")} right={hoursDisplay} onClick={openHoursSheet} />

        <Divider inset variant="outlineVariant" />

        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-body-large text-on-surface">
            {t("prescriptions.wizard.doses", "Doses")}
          </div>
          <div className="text-body-large text-primary flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={draft.schedule?.dosesPerTime ?? "1"}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  schedule: { ...d.schedule, dosesPerTime: e.target.value },
                }))
              }
              className="w-10 text-right bg-transparent outline-none text-body-large text-primary"
            />
            <span>{doseUnit}</span>
          </div>
        </div>
      </Card>

      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.cycle", "Cycle")}
      </div>
      <Card>
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-body-large text-on-surface">
            {t("prescriptions.wizard.useFor", "Use for")}
          </div>
          <div className="text-body-large text-primary flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={draft.schedule?.cycleUseDays ?? ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  schedule: {
                    ...d.schedule,
                    cycleUseDays: e.target.value === "" ? "" : Number(e.target.value),
                  },
                }))
              }
              className="w-14 text-right bg-transparent outline-none text-body-large text-primary"
            />
            <span>{t("prescriptions.wizard.days.label", "Days")}</span>
          </div>
        </div>

        <Divider inset variant="outlineVariant" />

        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-body-large text-on-surface">
            {t("prescriptions.wizard.pauseFor", "Pause for")}
          </div>
          <div className="text-body-large text-primary flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={draft.schedule?.cyclePauseDays ?? ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  schedule: {
                    ...d.schedule,
                    cyclePauseDays: e.target.value === "" ? "" : Number(e.target.value),
                  },
                }))
              }
              className="w-14 text-right bg-transparent outline-none text-body-large text-primary"
            />
            <span>{t("prescriptions.wizard.days.label", "Days")}</span>
          </div>
        </div>
      </Card>

    {/* Duration */}
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.duration", "Duration")}
      </div>
      <Card>
        <ClickRow
          left={t("prescriptions.wizard.startDate", "Start date")}
          right={
            draft.schedule?.startDateIso
              ? formatDateDisplay(String(draft.schedule.startDateIso).slice(0, 10))
              : t("prescriptions.wizard.select", "Select")
          }
          onClick={openStartDatePicker}
          trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
        />

        <Divider inset variant="outlineVariant" />

        <ClickRow
          left={t("prescriptions.wizard.endDate", "End date")}
          right={
            draft.schedule?.endDateIso
              ? formatDateDisplay(String(draft.schedule.endDateIso).slice(0, 10))
              : t("prescriptions.wizard.select", "Select")
          }
          onClick={openEndDatePicker}
          trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
        />
      </Card>
    </div>
  );
}

// Daily default
return (
  <div className="w-full space-y-3">
    <div className="text-label-large text-on-surface-var">
      {t("prescriptions.wizard.frequency.title", "Frequency")}
    </div>
    <Card>
      <ClickRow
        left={t("prescriptions.wizard.frequency.title", "Frequency")}
        right={selectRxFrequencyLabel({ schedule: draft?.schedule, t })}
        iconSrc={ICONS.FREQUENCY}
        rightIconSrc={ICONS.ELLIPSIS_VERTICAL}
        onClick={openFrequencySheet}
        />
      <Divider inset variant="outlineVariant" />
      <Row left={t("prescriptions.wizard.every", "Every")} right={t("prescriptions.wizard.day", "Day")} />
    </Card>

    <div className="text-label-large text-on-surface-var">
      {t("prescriptions.wizard.timeAndDoses", "Time & Doses")}
    </div>
    <Card>
      <Row left={t("prescriptions.wizard.time", "Time")} right={t("prescriptions.wizard.cyclicalShort", "Cyclical")} iconSrc={ICONS.TIME} />
      <Divider inset variant="outlineVariant" />

      <ClickRow left={t("prescriptions.wizard.every", "Every")} right={hoursDisplay} onClick={openHoursSheet} />

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.doses", "Doses")}
        </div>
        <div className="text-body-large text-primary flex items-center gap-2">
          <input
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={draft.schedule?.dosesPerTime ?? "1"}
            onChange={(e) =>
              setDraft((d) => ({
                ...d,
                schedule: { ...d.schedule, dosesPerTime: e.target.value },
              }))
            }
            className="w-10 text-right bg-transparent outline-none text-body-large text-primary"
          />
          <span>{doseUnit}</span>
        </div>
      </div>
    </Card>

  {/* Duration */}
    <div className="text-label-large text-on-surface-var">
      {t("prescriptions.wizard.duration", "Duration")}
    </div>
    <Card>
      <ClickRow
          left={t("prescriptions.wizard.startDate", "Start date")}
        right={
          draft.schedule?.startDateIso
            ? formatDateDisplay(String(draft.schedule.startDateIso).slice(0, 10))
            : t("prescriptions.wizard.select", "Select")
        }
        onClick={openStartDatePicker}
        trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
      />

      <Divider inset variant="outlineVariant" />

      <ClickRow
        left={t("prescriptions.wizard.endDate", "End date")}
        right={
          draft.schedule?.endDateIso
            ? formatDateDisplay(String(draft.schedule.endDateIso).slice(0, 10))
            : t("prescriptions.wizard.select", "Select")
        }
        onClick={openEndDatePicker}
        trailingIconSrc={ICONS.ELLIPSIS_VERTICAL}
      />
    </Card>
  </div>
);
})();

// ---- Step 7: Review + Notes ----

const Step7 = (
  <div className="w-full space-y-3">
    <div className="text-title-medium text-on-surface-var">
      {t("prescriptions.wizard.schedule", "Schedule")}
    </div>

    <Card className="overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.frequency.title", "Frequency")}
        </div>
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {selectRxFrequencyLabel({ schedule: draft?.schedule, t })}
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.dosage", "Dosage")}
        </div>
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {dosageLabel || "—"}
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.starts", "Starts")}
        </div>
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {draft?.schedule?.startDateIso ? formatDateDisplay(String(draft.schedule.startDateIso).slice(0, 10)) : "—"}
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.ends", "Ends")}
        </div>
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {draft?.schedule?.endDateIso ? formatDateDisplay(String(draft.schedule.endDateIso).slice(0, 10)) : "—"}
        </div>
      </div>
    </Card>

    {/* Notes box: NO "Notes" headline (blueprint) */}
    <Card className="overflow-hidden">
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t("prescriptions.wizard.optionalNotes", "Optional notes for the patient")}
        className="w-full min-h-[120px] bg-transparent p-4 outline-none text-body-medium text-on-surface placeholder:text-on-surface-var"
        style={{ resize: "none" }}
      />
    </Card>
  </div>
);

// ---- Step 8: Sign & Prescribe ----

const Step8 = (
  <div className="w-full space-y-3">
    <div className="text-title-medium text-on-surface-var">
      {t("prescriptions.wizard.schedule", "Schedule")}
    </div>

    <Card className="overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.frequency.title", "Frequency")}
        </div>
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {selectRxFrequencyLabel({ schedule: draft?.schedule, t })}
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.dosage", "Dosage")}
        </div>
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {dosageLabel || "—"}
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.starts", "Starts")}
        </div>
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {draft?.schedule?.startDateIso ? formatDateDisplay(String(draft.schedule.startDateIso).slice(0, 10)) : "—"}
        </div>
      </div>

      <Divider inset variant="outlineVariant" />

      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="text-body-large text-on-surface">
          {t("prescriptions.wizard.ends", "Ends")}
        </div>
        <div className="text-body-large text-primary min-w-0 text-right" style={truncateOneLineStyle()}>
          {draft?.schedule?.endDateIso ? formatDateDisplay(String(draft.schedule.endDateIso).slice(0, 10)) : "—"}
        </div>
      </div>
    </Card>

    {/* Notes: ✅ still editable on Step 8 */}
    <Card className="overflow-hidden">
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t("prescriptions.wizard.optionalNotes", "Optional notes for the patient")}
        className="w-full min-h-[120px] bg-transparent p-4 outline-none text-body-medium text-on-surface placeholder:text-on-surface-var"
        style={{ resize: "none" }}
      />
    </Card>

  {/* ✅ Signature capture (draw OR upload/camera) */}
  <DoctorSignatureCaptureCard
    value={doctor.signatureSrc || ""}
    onChange={(nextSrc) => {
      if (!nextSrc) setSignaturePersisted(false);
      setDraft((d) => ({
        ...d,
        doctor: { ...(d.doctor || {}), signatureSrc: nextSrc || "" },
      }));
    }}
    onSaved={({ persisted }) => {
      setSignaturePersisted(Boolean(persisted));
    }}
  />

  {!signaturePersisted && !devSigSrc ? (
    <div role="alert" className="rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
      <div className="text-title-small">{t("prescriptions.wizard.errors.signatureRequiredTitle", "Signature required")}</div>
      <div className="mt-1">{t("prescriptions.wizard.errors.signatureRequiredBody", "Add and save your signature before issuing this prescription.")}</div>
    </div>
  ) : null}

  {/* Prescriber */}
    <div className="text-label-medium text-on-surface-var">
      {t("prescriptions.wizard.prescriber", "Prescriber")}:{" "}
      <span className="text-on-surface">{doctorPrescriptionName}</span>
    </div>

  {/* Signature line + stamp (anchored) */}
    <SignatureStampLine
      sigSrc={doctor.signatureSrc || SIG_PLACEHOLDER}
      stampBaseSrc={DR_STAMP_BASE}
      doctorName={doctorPrescriptionName}
      regNo={doctor.regNo}
      stampSizePx={96}
      sigClassName="h-[68px] w-[160px]"
      // tiny nudges if you ever need them (start with 2/2 or 0/0)
      sigNudgeY={2}
      stampNudgeY={2}
    />

    <div className="mt-3 text-body-small text-on-surface-var">
      {t(
        "prescriptions.wizard.poweredBy",
        "Powered by AfriMed™. The prescriber remains responsible for clinical decisions.",
      )}
    </div>
  </div>
);

const StepSetup = (
  <div className="w-full space-y-4">
    <ClinicalActionPanel appointmentId={appointmentId} kinds={['prescription']} onReviewed={action => {
      setNotes(action.content.text || '');
      const query = new URLSearchParams(location.search);
      query.set('clinicalActionId', action.id);
      nav({pathname: location.pathname, search: query.toString()}, {replace: true});
    }} />
    <Card className="p-4">
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.diagnosis", "Diagnosis")}
      </div>
      <input
        value={draft.diagnosisTitle || ""}
        onChange={(e) =>
          setDraft((d) => ({
            ...d,
            diagnosisTitle: e.target.value,
            updatedAtIso: nowIso(),
          }))
        }
        placeholder={t("prescriptions.wizard.diagnosisPlaceholder", "Add the diagnosis")}
        className="mt-2 w-full bg-transparent outline-none text-title-medium text-on-surface"
      />
      <div className="mt-4 text-label-large text-on-surface-var">
        {t("prescriptions.wizard.notes", "Notes")}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t("prescriptions.wizard.optionalNotes", "Optional notes for the patient")}
        className="mt-2 min-h-[96px] w-full bg-transparent outline-none text-body-medium text-on-surface placeholder:text-on-surface-var"
        style={{ resize: "none" }}
      />
    </Card>

    <div>
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.medication", "Medication")}
      </div>
      <div className="mt-3 h-14 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.06)] px-4 flex items-center gap-3">
        <img
          src={ICONS.SEARCH}
          alt=""
          className="w-6 h-6"
          draggable="false"
          style={
            isDark
              ? { filter: "brightness(0) saturate(100%) invert(80%) sepia(8%) saturate(348%) hue-rotate(182deg) brightness(89%) contrast(87%)" }
              : undefined
          }
        />

        <input
          value={draft.medicationQuery}
          onChange={(e) => {
            const nextQ = e.target.value;
            setDraft((d) => ({
              ...d,
              medicationQuery: nextQ,
              medication: {
                ...d.medication,
                id: d.medication?.id || "",
                name: d.medication?.name && normName(d.medication?.name) === normName(nextQ) ? d.medication.name : "",
              },
            }));
          }}
          placeholder={t("prescriptions.wizard.searchOrAdd", "Search or add")}
          className="flex-1 bg-transparent outline-none text-body-large text-on-surface"
        />

        <button
          type="button"
          onClick={addCustomMedicationFromQuery}
          className="shrink-0 grid place-items-center rounded-full w-8 h-8 border border-[rgba(24,50,119,0.16)] hover:bg-[rgba(24,50,119,0.04)] focus:outline-none focus:ring-2 focus:ring-[rgba(24,50,119,0.24)]"
          aria-label={t("prescriptions.wizard.addMedication", "Add medication")}
        >
          <img src={ICONS.ADD_TWO} alt="" className="h-5 w-5" draggable="false" />
        </button>

        {!!draft.medicationQuery && (
          <button
            type="button"
            aria-label={t("common.actions.clear", "Clear")}
            onClick={() => setDraft((d) => ({ ...d, medicationQuery: "", medication: { ...d.medication, name: "" } }))}
            className="h-10 w-10 flex items-center justify-center"
          >
            <img src={ICONS.CANCEL} alt="" className="w-5 h-5" draggable="false" />
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => setImagesSheetOpen(true)}
        className="mt-3 rounded-xl bg-[rgba(77,163,255,0.12)] px-4 py-3 inline-flex items-center gap-2"
      >
        <span className="inline-flex h-4 w-4 items-center justify-center text-primary">
          <span className="text-body-large">＋</span>
        </span>
        <span className="text-primary text-label-large">
          {t("prescriptions.wizard.uploadImage", "Upload image")}
        </span>
      </button>

      <div className="mt-3">
        <ReferenceImagesSection editable />
      </div>
    </div>

    {savedMedications.length ? (
      <Card className="p-4">
        <div className="text-label-large text-on-surface-var">
          {t("prescriptions.wizard.savedMedicines", "Saved medicines")}
        </div>
        <div className="mt-2 text-body-medium text-on-surface">
          {savedMedications.map((item) => item.name).join(", ")}
        </div>
      </Card>
    ) : null}

    {draft.medicationQuery?.trim() ? (
      <>
        <div className="text-body-medium text-on-surface-var">
          {t("prescriptions.wizard.possibleMatches", "Possible matches")}
        </div>

        <Card>
          {medLoading ? (
            <div className="px-4 py-4 text-body-medium text-on-surface-var">
              {t("prescriptions.wizard.loadingMatches", "Loading… one moment please!")}
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-4 text-body-medium text-on-surface-var">
              {t(
                "prescriptions.wizard.noMatchesAdd",
                "No matches. Tap + to add “{query}”.",
                { query: (draft.medicationQuery || "").trim() },
              )}
            </div>
          ) : (
            results.map((r, idx) => {
              const active = draft.medication?.name === r.name;
              return (
                <div key={`${r.name}-${idx}`}>
                  <button
                    type="button"
                    className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left"
                    onClick={() =>
                      setDraft((d) => {
                        const inferredStrength = extractMedicationStrength(r.composition, r.name);
                        return {
                          ...d,
                          medicationQuery: r.name,
                          medication: {
                            ...d.medication,
                            id: d.medication?.id || "",
                            name: r.name,
                            composition: r.composition,
                            uses: r.uses,
                            sideEffects: r.sideEffects,
                            imageUrl: r.imageUrl,
                            manufacturer: r.manufacturer,
                            strengthValue:
                              String(d?.medication?.strengthValue || "").trim() ||
                              inferredStrength?.value ||
                              "",
                            strengthUnit:
                              String(d?.medication?.strengthUnit || "").trim() ||
                              inferredStrength?.unit ||
                              d?.medication?.strengthUnit ||
                              "mg",
                          },
                        };
                      })
                    }
                  >
                    <div className="min-w-0">
                      <div className="text-body-medium text-on-surface" style={truncateOneLineStyle()}>
                        {r.name}
                      </div>
                      <div className="text-body-medium text-on-surface-var" style={truncateOneLineStyle()}>
                        {r.composition}
                      </div>
                    </div>
                    <Radio active={active} />
                  </button>
                  {idx !== results.length - 1 ? <Divider inset variant="outlineVariant" /> : null}
                </div>
              );
            })
          )}
        </Card>
      </>
    ) : null}
  </div>
);

const StepBuilderScreen = (
  <div className="w-full space-y-4">
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-title-medium text-on-surface">
            {draft.medication?.name || t("prescriptions.wizard.medication", "Medication")}
          </div>
          <div className="mt-1 text-body-medium text-on-surface-var">
            {draft.medication?.composition || t("prescriptions.wizard.builderHelp", "Complete the medicine details, then save it to the prescription.")}
          </div>
        </div>
        <button
          type="button"
          className="text-label-large text-primary"
          onClick={() => setStep(0)}
        >
          {t("common.change", "Change")}
        </button>
      </div>
    </Card>

    {Step3}
    {Step4}
    {Step6}

    <Card className="p-4">
      <div className="text-title-medium text-on-surface-var">
        {t("prescriptions.wizard.medicationNote", "Medicine note")}
      </div>
      <textarea
        value={draft.medication?.note || ""}
        onChange={(e) =>
          setDraft((d) => ({
            ...d,
            medication: { ...d.medication, note: e.target.value },
            updatedAtIso: nowIso(),
          }))
        }
        placeholder={t("prescriptions.wizard.medicationNotePlaceholder", "Optional note for this medicine")}
        className="mt-3 min-h-[96px] w-full bg-transparent outline-none text-body-medium text-on-surface placeholder:text-on-surface-var"
        style={{ resize: "none" }}
      />
    </Card>
  </div>
);

const StepReviewScreen = (
  <div className="w-full space-y-4">
    <Card className="p-4">
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.diagnosis", "Diagnosis")}
      </div>
      <div className="mt-2 text-title-medium text-on-surface">
        {draft.diagnosisTitle || "—"}
      </div>
      <div className="mt-4 text-label-large text-on-surface-var">
        {t("prescriptions.wizard.notes", "Notes")}
      </div>
      <div className="mt-2 text-body-medium text-on-surface whitespace-pre-wrap">
        {notes || "—"}
      </div>
    </Card>

    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="text-label-large text-on-surface-var">
        {t("prescriptions.wizard.medicines", "Medicines")}
      </div>
      <button
        type="button"
        className="inline-flex items-center rounded-xl bg-[rgba(77,163,255,0.12)] px-4 py-2.5 text-label-large text-primary"
        onClick={() => {
          resetMedicationEditor();
          setStep(0);
        }}
      >
        {t("prescriptions.wizard.addAnotherMedicine", "Add another medicine")}
      </button>
    </div>

    <ReferenceImagesSection />

    {savedMedications.map((item, index) => {
      const schedule = item.schedule || {};
      const frequencyType = String(schedule.frequencyType || item.frequencyType || "").trim().toLowerCase();
      const frequencyValue =
        frequencyType === "daily"
          ? t("prescriptions.wizard.frequency.daily", "Daily")
          : selectRxFrequencyLabel({ schedule, t }) || item.frequencyLabel || item.frequency || "—";
      const intervalHours = Number(schedule.everyHours || item.everyHours || 0);
      const scheduleReviewRows = [
        {
          label: t("prescriptions.wizard.frequency", "Frequency"),
          value: frequencyValue,
        },
        ...(frequencyType === "daily" && intervalHours > 0
          ? [{
              label: t("prescriptions.wizard.interval", "Interval"),
              value: `${t("prescriptions.wizard.every", "Every")} ${t(
                "prescriptions.wizard.hoursValue",
                "{count} hours",
                { count: intervalHours },
              )}`,
            }]
          : []),
        {
          label: t("prescriptions.wizard.dosage", "Dosage"),
          value: buildPrescriptionDosageLabel(item, { t, languageCode }) || item.strengthLabel || "—",
        },
        {
          label: t("prescriptions.wizard.starts", "Starts"),
          value: formatDateDisplay(
            item.startsOn || item.startDateIso || item.meta?.startsOn || schedule.startDateIso,
          ) || "—",
        },
        {
          label: t("prescriptions.wizard.ends", "Ends"),
          value: formatDateDisplay(
            item.endsOn || item.endDateIso || item.meta?.endsOn || schedule.endDateIso,
          ) || "—",
        },
      ];
      return (
        <Card key={item.id} className="overflow-hidden">
          <div className="px-4 py-4">
            <div className="min-w-0">
                <div className="text-title-medium text-on-surface">
                  {item.name}
                </div>
                <div className="mt-1 text-body-medium text-on-surface-var">
                  {[item.typeLabel, item.strengthLabel].filter(Boolean).join(" • ") || item.composition || "—"}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-label-medium">
                <button
                  type="button"
                  className="text-primary"
                  onClick={() => {
                    hydrateEditorFromMedication(item);
                    setStep(STEP_BUILDER);
                  }}
                >
                  {t("common.edit", "Edit")}
                </button>
                <button
                  type="button"
                  onClick={() => moveMedication(item.id, -1)}
                  disabled={index === 0}
                  className={index === 0 ? "text-on-surface-var opacity-40" : "text-on-surface-var"}
                >
                  {t("prescriptions.wizard.moveUp", "Move up")}
                </button>
                <button
                  type="button"
                  onClick={() => moveMedication(item.id, 1)}
                  disabled={index === savedMedications.length - 1}
                  className={
                    index === savedMedications.length - 1
                      ? "text-on-surface-var opacity-40"
                      : "text-on-surface-var"
                  }
                >
                  {t("prescriptions.wizard.moveDown", "Move down")}
                </button>
                <button
                  type="button"
                  className="text-[rgb(var(--schemes-critical))]"
                  onClick={() => removeMedicationCard(item.id)}
                >
                  {t("common.delete", "Delete")}
                </button>
              </div>
            </div>

            {item.composition ? (
              <div className="mt-3 text-body-medium text-on-surface-var">{item.composition}</div>
            ) : null}
            {item.note ? (
              <div className="mt-3 text-body-medium text-on-surface whitespace-pre-wrap">{item.note}</div>
            ) : null}
          </div>

          {scheduleReviewRows.length ? <Divider inset variant="outlineVariant" /> : null}

          {scheduleReviewRows.map((row, rowIndex) => (
            <div key={`${item.id}-row-${rowIndex}`}>
              <div className="flex items-start justify-between gap-5 px-4 py-3">
                <div className="shrink-0 text-body-medium text-on-surface-var">{row.label}</div>
                <div className="min-w-0 text-right text-body-medium text-on-surface break-words">
                  {row.value}
                </div>
              </div>
              {rowIndex < scheduleReviewRows.length - 1 ? <Divider inset variant="outlineVariant" /> : null}
            </div>
          ))}
        </Card>
      );
    })}
  </div>
);

const StepSignScreen = (
  <div className="w-full space-y-4">
    <Card className="p-4">
      <div className="text-title-medium text-on-surface-var">
        {t("prescriptions.wizard.reviewPrescription", "Review prescription")}
      </div>
      <div className="mt-3 text-body-medium text-on-surface">
        {draft.diagnosisTitle || "—"}
      </div>
      <div className="mt-2 text-body-medium text-on-surface-var whitespace-pre-wrap">
        {notes || "—"}
      </div>
      <div className="mt-4 text-label-large text-on-surface-var">
        {t("prescriptions.wizard.medicinesCount", "{count} medicine(s)", {
          count: savedMedications.length,
        })}
      </div>
    </Card>

    <ReferenceImagesSection />

    <Card className="overflow-hidden">
      {savedMedications.map((item, index) => (
        <div key={item.id}>
          <div className="px-4 py-4">
            <div className="text-title-medium text-on-surface">{item.name}</div>
            <div className="mt-1 text-body-medium text-on-surface-var">
              {[item.typeLabel, item.strengthLabel, item.frequencyLabel].filter(Boolean).join(" • ")}
            </div>
            {item.note ? (
              <div className="mt-2 text-body-medium text-on-surface whitespace-pre-wrap">{item.note}</div>
            ) : null}
          </div>
          {index < savedMedications.length - 1 ? <Divider inset variant="outlineVariant" /> : null}
        </div>
      ))}
    </Card>

    <DoctorSignatureCaptureCard
      value={doctor.signatureSrc || ""}
      onChange={(nextSrc) => {
        if (!nextSrc) setSignaturePersisted(false);
        setDraft((d) => ({
          ...d,
          doctor: { ...(d.doctor || {}), signatureSrc: nextSrc || "" },
        }));
      }}
      onSaved={({ persisted }) => {
        setSignaturePersisted(Boolean(persisted));
      }}
      embedded
      flush
      canvasSurface="card"
      inlineActions
      saveLabel={t("common.save", "Save")}
    />

    <div className="text-label-medium text-on-surface-var">
      {t("prescriptions.wizard.prescriber", "Prescriber")}:{" "}
      <span className="text-on-surface">{doctorPrescriptionName}</span>
    </div>

    <SignatureStampLine
      sigSrc={doctor.signatureSrc || SIG_PLACEHOLDER}
      stampBaseSrc={DR_STAMP_BASE}
      doctorName={doctorPrescriptionName}
      regNo={doctor.regNo}
      stampSizePx={96}
      sigClassName="h-[68px] w-[160px]"
      sigNudgeY={2}
      stampNudgeY={2}
    />

    <div className="mt-3 text-body-small text-on-surface-var">
      {t(
        "prescriptions.wizard.poweredBy",
        "Powered by AfriMed™. The prescriber remains responsible for clinical decisions.",
      )}
    </div>
  </div>
);

// Restored prescription composition: TopBarRx sits over the cover and keeps its
// intentional controls-to-headline rhythm. The local stylesheet only restores
// the current AppShell's mobile full-bleed boundary.
  const useHeroRxHeader = showCover && step !== STEP_UNIT;
  const coverHeightClass = "h-[190px] sm:h-[220px] md:h-[280px] lg:h-[320px]";
  const coverOverlapClass = showCover
    ? step === 0 || step === STEP_BUILDER
      ? "-mt-[32px] sm:-mt-[40px] md:-mt-[56px] lg:-mt-[72px]"
      : "-mt-[24px] sm:-mt-[32px] md:-mt-[44px] lg:-mt-[56px]"
    : "";
  const contentStartClass = !useHeroRxHeader
    ? "mt-4 md:mt-5 lg:mt-6"
    : step === 0 || step === STEP_BUILDER
      ? "mt-1 md:mt-2 lg:mt-3"
      : "mt-0 md:mt-1 lg:mt-2";

// -------------------------
// Unit list (search filter)
// -------------------------

return (
  <div className="min-h-[100svh] rxwiz">
    {/* Cover */}
    {showCover ? (
      <div
        className="prescription-cover-bleed relative w-full -mt-[16px] md:-mt-[20px]"
        data-testid="prescription-cover"
      >
        <div className="absolute inset-x-0 top-0 z-0 overflow-hidden rounded-none">
          <div className={`relative ${coverHeightClass} w-full overflow-hidden rounded-none bg-[rgb(var(--clr-surface-container-low))]`}>
            <img
              src={isDark ? PRESCRIPTION_BG_DARK : PRESCRIPTION_BG_LIGHT}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-top"
              draggable="false"
            />
          </div>
        </div>
        {useHeroRxHeader ? (
          <div className="absolute inset-x-0 top-0 z-10">
            <div className="prescription-header-rail afrimed-rail afrimed-rail--comfortable pt-3 md:pt-4 lg:pt-5">
              {Header}
            </div>
          </div>
        ) : null}
        {/* spacer to reserve cover height */}
        <div className={coverHeightClass} />
      </div>
    ) : null}

  {/* Content rail */}
    <div className={`prescription-content-shell relative z-10 ${coverOverlapClass}`}>
      <div className="prescription-route-rail min-w-0 max-w-full pt-4 pb-24" data-testid="prescription-content-rail">
        {useHeroRxHeader ? null : step === STEP_UNIT ? null : Header}

        <div
          className={`prescription-content-inner ${contentStartClass} flex flex-col min-h-0`}
        >
          {issueErr ? (
            <div className="mb-3 rounded-[12px] border border-[rgba(186,26,26,0.18)] bg-[rgba(186,26,26,0.08)] px-3 py-2 text-body-medium text-[rgb(var(--schemes-critical))]">
              {issueErr}
            </div>
          ) : null}

          <div className="flex-1 min-h-0">
            {step === 0 && StepSetup}
            {step === STEP_BUILDER && StepBuilderScreen}
            {step === STEP_REVIEW && StepReviewScreen}
            {step === STEP_SIGN && StepSignScreen}
            {step === STEP_UNIT && StepChooseUnit}
          </div>
        </div>
      </div>
    </div>

  {/* QR overlay (mobile sheet + desktop dialog) */}
  <QrOverlay
  open={qrOpen}
  onClose={() => setQrOpen(false)}
  rx={rx}
  doctorName={doctorPrescriptionName}
  qrSrc={qrSrc}
  qrElephantSrc={QR_ELEPHANT}
  sigPlaceholderSrc={SIG_PLACEHOLDER}
  stampBaseSrc={abs(DR_STAMP_BASE)}
  verifyPath={verifyPath}
  verifyUrl={qrValue}
  verifyState={verifyState}
/>

  {/* bottom CTA */}
    {step !== STEP_UNIT ? (
      <div
        className="prescription-sticky-footer afrimed-sticky-footer bg-[rgb(var(--clr-surface-cont-highest))]"
        data-testid="prescription-sticky-footer"
      >
        <div
          className="prescription-footer-cta-rail afrimed-rail afrimed-rail--comfortable pt-3"
        >
          {/* centered CTA (no tab background) */}
          <div className="afrimed-cta-wrap afrimed-cta-wrap--wide">
            <Button size="lg" disabled={!canContinue() || issueBusy} onClick={onContinue}>
              {step === STEP_BUILDER ? (
                t("prescriptions.wizard.saveMedicine", "Save medicine")
              ) : step === STEP_REVIEW ? (
                <span className="inline-flex items-center gap-2">
                  <img src={ICON_SIGN} alt="" className="h-5 w-5" draggable="false" />
                  {t("prescriptions.wizard.signAndPrescribe", "Sign & Prescribe")}
                </span>
              ) : step === STEP_SIGN ? (
                issueBusy
                  ? t("prescriptions.wizard.issuing", "Issuing...")
                  : issueDone
                    ? t("prescriptions.wizard.viewPrescription", "View prescription")
                    : t("common.actions.done", "Done")
              ) : (
                t("common.actions.continue", "Continue")
              )}
            </Button>
          </div>
        </div>
      </div>
    ) : null}

  {/* Hidden file inputs */}
    <input
  ref={galleryInputRef}
  type="file"
  accept="image/*"
  multiple
  className="hidden"
  onChange={(e) => {
    const files = e.target.files;
    if (!files?.length) return;

    if (filePickTarget === "signature") {
      const f = files[0];
      readFileAsDataUrl(f).then((src) => {
        setDraft((d) => ({
          ...d,
          doctor: { ...(d.doctor || {}), signatureSrc: src || "" },
        }));
      });
      setFilePickTarget(null);
    } else {
      onFilesPicked(files, "upload");
    }

    e.target.value = "";
  }}
/>

<input
  ref={cameraInputRef}
  type="file"
  accept="image/*"
  capture="environment"
  className="hidden"
  onChange={(e) => {
    const files = e.target.files;
    if (!files?.length) return;

    if (filePickTarget === "signature") {
      const f = files[0];
      readFileAsDataUrl(f).then((src) => {
        setDraft((d) => ({
          ...d,
          doctor: { ...(d.doctor || {}), signatureSrc: src || "" },
        }));
      });
      setFilePickTarget(null);
    } else {
      onFilesPicked(files, "camera");
    }

    e.target.value = "";
  }}
/>

{/* Step 1: Add images sheet (Sheet primitive) */}
<Sheet open={imagesSheetOpen} onClose={() => setImagesSheetOpen(false)}>
  <div className="pt-6 px-[var(--rail-pad)]">
    {/* title already sits inside Sheet padding */}
    <div className="text-title-large text-on-surface">
      {t("prescriptions.wizard.addImagesToGallery", "Add images to gallery")}
    </div>

    <div className="mt-6">
      <button
        type="button"
        className="w-full py-4 flex items-center justify-between text-left"
        onClick={() => {
          setImagesSheetOpen(false);
          galleryInputRef.current?.click?.();
        }}
      >
        <div className="flex items-center gap-3">
          <img src={ICONS.IMAGESMODE} alt="" className="w-6 h-6" draggable="false" />
          <div className="text-body-large text-on-surface">
            {t("prescriptions.wizard.uploadFromGallery", "Upload from gallery")}
          </div>
        </div>
        <img src={ICONS.NAVIGATE_NEXT} alt="" className="w-6 h-6" draggable="false" />
      </button>

      <Divider variant="outlineVariant" />

      <button
        type="button"
        className="w-full py-4 flex items-center justify-between text-left"
        onClick={() => {
          setImagesSheetOpen(false);
          cameraInputRef.current?.click?.();
        }}
      >
        <div className="flex items-center gap-3">
          <img src={ICONS.PHOTO_CAMERA} alt="" className="w-6 h-6" draggable="false" />
          <div className="text-body-large text-on-surface">
            {t("prescriptions.wizard.takePhoto", "Take photo")}
          </div>
        </div>
        <img src={ICONS.NAVIGATE_NEXT} alt="" className="w-6 h-6" draggable="false" />
      </button>
    </div>

    <div className="h-10" />
  </div>
</Sheet>

  {/* Choose Unit (Sheet primitive + TopBarCenter + SaveChip) */}
    <Sheet open={unitSheetOpen} onClose={() => setUnitSheetOpen(false)}>
      <div className="px-0 pt-0">
        <TopBarCenter
          title={t("prescriptions.wizard.chooseUnit", "Choose Unit")}
          onBack={() => setUnitSheetOpen(false)}
          trailing={
            <SaveChip
              disabled={!unitDraft}
              onClick={() => {
                setDraft((d) => ({
                  ...d,
                  medication: { ...d.medication, strengthUnit: unitDraft || "mg" },
                }));
                setUnitSheetOpen(false);
              }}
            >
              Save
            </SaveChip>
          }
        />

        {/* Search row */}
        <div className="h-12 rounded-[12px] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_1px_2px_rgba(15,23,42,0.06)] px-4 flex items-center gap-3">
          <img src={ICONS.SEARCH} alt="" className="w-6 h-6" draggable="false" />
          <input
            value={unitSearch}
            onChange={(e) => setUnitSearch(e.target.value)}
            placeholder={t("common.search", "Search")}
            className="flex-1 bg-transparent outline-none text-body-large text-on-surface"
          />
          {unitSearch ? (
            <button
              type="button"
              className="h-9 w-9 flex items-center justify-center"
              aria-label={t("common.actions.clearSearch", "Clear search")}
              onClick={() => setUnitSearch("")}
            >
              <img src={ICONS.CANCEL} alt="" className="w-6 h-6" draggable="false" />
            </button>
          ) : null}
        </div>

        {/* ✅ Unit list card (THIS is correct and must stay inside unit sheet) */}
        <div className="mt-3">
          <Card>
            {unitList.map((u, idx) => {
              const active = (unitDraft || "mg") === u;
              return (
                <div key={u}>
                  <button
                    type="button"
                    className="w-full px-4 py-3 flex items-center justify-between text-left"
                    onClick={() => setUnitDraft(u)}
                  >
                    <div className="text-body-large text-on-surface">{u}</div>
                    <Radio active={active} />
                  </button>
                  {idx !== unitList.length - 1 ? <Divider inset variant="outlineVariant" /> : null}
                </div>
              );
            })}
          </Card>
        </div>

        <div className="h-10" />
      </div>
    </Sheet>

{/* Start/End date picker */}
<Sheet open={dateSheet !== null} onClose={closeDatePicker}>
  <div className="pt-4 pb-10">
    {/* Header padded to rail */}
    <div className="px-[var(--rail-pad)]">
      <TopBarCenter
        title={
          dateSheet === "start"
            ? t("prescriptions.wizard.startDate", "Start date")
            : t("prescriptions.wizard.endDate", "End date")
        }
        onBack={closeDatePicker}
        close
        right={
          <SaveChip
            disabled={!dateDraft}
            onClick={() => {
              if (!dateDraft) return;

              setDraft((d) => ({
                ...d,
                schedule: {
                  ...d.schedule,
                  ...(dateSheet === "start"
                    ? { startDateIso: dateDraft }
                    : { endDateIso: dateDraft }),
                },
              }));

              closeDatePicker();
            }}
          >
            Save
          </SaveChip>
        }
      />
    </div>

    {/* Calendar padded to rail */}
    <div className="px-[var(--rail-pad)] mt-3">
      <DatePicker
        value={(() => {
          if (!dateDraft) return new Date();
          // parse YYYY-MM-DD as LOCAL date (prevents off-by-1 on some devices)
          const [yy, mm, dd] = dateDraft.split("-").map(Number);
          return new Date(yy, (mm || 1) - 1, dd || 1);
        })()}
        onChange={(dt) => {
          const y = dt.getFullYear();
          const m = String(dt.getMonth() + 1).padStart(2, "0");
          const d = String(dt.getDate()).padStart(2, "0");
          setDateDraft(`${y}-${m}-${d}`);
        }}
        onCancel={closeDatePicker}
        mode="single"
      />
    </div>
  </div>
</Sheet>

{/* activeSheet sibling sheets go BELOW here */}

  {/* =========================
     Sheets (siblings) — MUST be INSIDE the main return
     ========================= */}
  <>
{/* Frequency picker */}
<Sheet open={activeSheet === "frequency"} onClose={closeActiveSheet}>
  <div className="pt-2 pb-10">
    <div className="px-[var(--rail-pad)]">
      <TopBarCenter title={t("prescriptions.wizard.frequency.title", "Frequency")} onBack={closeActiveSheet} close />
    </div>

    <div className="mt-2">
      {frequencyOptions.map((opt, idx, arr) => {
        const active = draft.schedule?.frequencyType === opt.key;

        return (
          <div key={opt.key}>
            <button
              type="button"
              className="w-full px-[var(--rail-pad)] py-4 flex items-center justify-between text-left"
              onClick={() => {
                setDraft((d) => ({
                  ...d,
                  schedule: { ...d.schedule, frequencyType: opt.key },
                }));
                closeActiveSheet();
              }}
            >
              <div className="text-body-large text-on-surface">{opt.label}</div>
              <Radio active={active} />
            </button>

            {idx !== arr.length - 0 ? <Divider inset variant="outlineVariant" /> : null}
          </div>
        );
      })}
    </div>

    <div className="h-10" />
  </div>
</Sheet>

{/* Hours picker */}
<Sheet open={activeSheet === "hours"} onClose={closeActiveSheet}>
  <div className="pt-2 pb-10">
    <div className="px-[var(--rail-pad)]">
      <TopBarCenter title={t("prescriptions.wizard.timeInterval", "Time interval")} onBack={closeActiveSheet} close />
    </div>

    <div className="mt-2">
      {[1, 2, 3, 4, 6, 8, 12, 24].map((h, idx, arr) => {
        const active = Number(draft.schedule?.everyHours || 0) === h;

        return (
          <div key={h}>
            <button
              type="button"
              className="w-full px-[var(--rail-pad)] py-4 flex items-center justify-between text-left"
              onClick={() => {
                setDraft((d) => ({
                  ...d,
                  schedule: { ...d.schedule, everyHours: h },
                }));
                closeActiveSheet();
              }}
            >
              <div className="text-body-large text-on-surface">
                {t("prescriptions.wizard.hoursValue", "{count} hours", { count: h })}
              </div>
              <Radio active={active} />
            </button>

            {idx !== arr.length - 0 ? <Divider inset variant="outlineVariant" /> : null}
          </div>
        );
      })}
    </div>

    <div className="h-10" />
  </div>
</Sheet>

{/* Specific days picker (multi-select, autosave, user-controlled close) */}
    <Sheet open={activeSheet === "days"} onClose={closeActiveSheet}>
      <div className="pt-2 pb-10 overflow-x-hidden">
        <div className="px-[var(--rail-pad)]">
          <TopBarCenter title={t("prescriptions.wizard.days.title", "Days of the week")} onBack={closeActiveSheet} close />
        </div>

        <div className="mt-2">
          {dayOptions.map((day, idx, arr) => {
            const active = daysDraft.includes(day.key);

            return (
              <div key={day.key}>
                <button
                  type="button"
                  className="w-full px-[var(--rail-pad)] py-4 flex items-center justify-between text-left"
                  onClick={() => {
                    setDaysDraft((prev) => {
                      const next = prev.includes(day.key)
                        ? prev.filter((d) => d !== day.key)
                        : [...prev, day.key];

                      setDraft((d) => ({
                        ...d,
                        schedule: { ...d.schedule, specificDays: next },
                      }));

                      return next;
                    });
                  }}
                >
                  <div className="text-body-large text-on-surface">{day.label}</div>
                  <Radio active={active} />
                </button>

                {idx !== arr.length - 0 ? <Divider inset variant="outlineVariant" /> : null}
              </div>
            );
          })}
        </div>

        <div className="h-10" />
      </div>
    </Sheet>
    </>
  </div>
);
}

    
